import usePageTitle from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sb } from '../supabase';
import { getPrimaryProductImage } from '../lib/productMedia';
// The same predicate the two server-side readers use, mirroring
// public.request_is_curated(uuid): a managed / idea-to-product request is one
// we run ourselves, so the trader may stop it but never delete it.
import { isCuratedRequest } from '../lib/offerVisibility';
import { readSwrCache, writeSwrCache } from '../lib/useStaleWhileRevalidate';
import {
  getOfferEstimatedTotal,
  formatMoq,
  getOfferShippingMethod,
  hasOfferShippingCost,
} from '../lib/offerPricing';
import {
  buildSupplierTrustSignals,
  getSupplierMaabarId,
} from '../lib/supplierOnboarding';
import { PRODUCT_TIER_EMBED, deriveProductPriceFrom } from '../lib/productPriceLookup';
import { T } from '../lib/supplierDashboardConstants';
import {
  attachSupplierProfiles,
} from '../lib/profileVisibility';
import {
  fetchProductInquiryThreads,
  getProductInquiryProductName,
  getProductInquiryStatusLabel,
} from '../lib/productInquiry';
import Footer from '../components/Footer';
import OrderInvoiceModal from '../components/OrderInvoiceModal';
import TranslatedText from '../components/TranslatedText';
import BuyerHomePanel from '../components/buyer/BuyerHomePanel';
import { startTelrPayment, paymentStartErrorAlert } from '../lib/telrPay';
import { isManagedRequest, requestType } from '../lib/managedSourcing';
import { CANCELLED, isRequestCancelled, isRfqCollecting, requestStatusLabel } from '../lib/requestStatus';
// offers.origin is free text a supplier typed. "china" is a value, not a label.
import { countryOfManufacture } from '../lib/countryNames';
import { supplierEmailTitle, supplierEmailProductName } from '../lib/supplierEmailContent';
// The stage bar, extracted from this file so the admin console can render the
// SAME one rather than a third copy.
import { RequestTimeline } from '../lib/requestTimeline';

import { explainPriceBasis, deliveryLine, deliveryMismatchText } from '../lib/priceBasisExplainer';
import { compareDelivery } from '../lib/deliveryMatch';
import { offerValidityText, offerSampleText } from '../lib/offerValidity';
import { payoutAssurance } from './dashboardBuyerStrings';
import { paymentSplit, balanceDue, paidInFullLabel, fullPaymentLabel } from '../lib/paymentProgress';
import { fetchPaymentQuote, fetchOfferAmounts, stageAmountSar, feeCreditSar, formatSar, sarFixedNote } from '../lib/paymentQuote';
import { fetchRequestFees, payRequestFee, feeNoticeText, feeStatusText, feeCreditLabel } from '../lib/requestFee';
import { isPlainQuantity, normalizeQuantityInput, quantityMessage } from '../lib/requestQuantity';
import Notice from '../components/Notice';
import { useHioBuyOnly } from '../lib/marketMode';
import { navLabel } from '../lib/navItems';

/* A numeric run, isolated from the paragraph's direction.
   «تكلفة المنتجات: 150,000.00 CNY ≈ 83,896.22 SAR» renders SCRAMBLED in RTL:
   the run is digits, Latin currency codes and an ≈, and the bidi algorithm is
   entitled to reorder the pieces around the Arabic label, so the currency ends
   up beside the wrong number. dir="ltr" isolates the run, which makes ar render
   like en and zh by construction rather than by luck.
   Same device as Footer.jsx:26, written there for the CR and VAT lines and for
   the same reason. Kept as a local four-word component rather than shared: it
   is a bidi escape hatch, not a design token, and a shared "Num" invites being
   used for things that are not a run of numbers. */
const Num = ({ v }) => <span dir="ltr">{v}</span>;

const SEND_EMAILS_URL = 'https://utzalmszfqfcofywfetv.supabase.co/functions/v1/send-email';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0emFsbXN6ZnFmY29meXdmZXR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM2NjE4NDAsImV4cCI6MjA4OTIzNzg0MH0.SSqFCeBRhKRIrS8oQasBkTsZxSv7uZGCT9pqfK-YmX8';

const getTrackingUrl = (company, num) => {
  const urls = {
    'DHL':    `https://www.dhl.com/track?tracking-id=${num}`,
    'FedEx':  `https://www.fedex.com/tracking?tracknumbers=${num}`,
    'Aramex': `https://www.aramex.com/track/${num}`,
    'UPS':    `https://www.ups.com/track?tracknum=${num}`,
    'SMSA':   `https://www.smsaexpress.com/track?awbno=${num}`,
  };
  return urls[company] || `https://t.17track.net/en#nums=${num}`;
};


// A cancelled request is CLOSED, and every "is this still live" test has to say
// so. Missing it made a cancelled request count as Active and, if it carried
// pending offers, as needing action -- a row with no buttons telling the trader
// to do something about it.
//
// MOVED 2026-09-20 to src/lib/requestStatus.js and imported here. It was fixed
// on this page and nowhere else, so the trader's HOME FEED went on doing the
// exact thing this comment warns about for another day -- a cancelled managed
// request with a live stage bar, and a cancelled RFQ under "يحتاج تصرّفك".
// A lesson recorded in one file teaches the other files nothing; the constant
// has one definition now and buyerActionItems.js reads the same one.
const STATUS_STEPS = ['open', 'offers_received', 'closed', 'supplier_confirmed', 'paid', 'ready_to_ship', 'shipping', 'arrived', 'delivered'];

const CITIES_AR = ['الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر', 'تبوك', 'أبها', 'القصيم', 'حائل', 'جازان', 'نجران'];
const CITIES_EN = ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Tabuk', 'Abha', 'Qassim', 'Hail', 'Jazan', 'Najran'];

/* ─── Status Bar ─────────────────────────── */
// `status` here is the DISPLAY status, not requests.status. The two diverged
// visibly on M-10074: requests.status flips to 'offers_received' on any
// insert, including an offer gated because the supplier is unverified, so the
// header announced "عروض وصلت" while the body said "لا توجد عروض بعد" and every
// counter read zero. The gating was right; the header was reading a source the
// trader cannot see. Callers now pass a status derived from VISIBLE offers.
// NOTE: dead as of 2026-09-21 -- defined here, called nowhere. Pointed at the
// shared label helper anyway rather than left as the last reason to keep a
// second status map alive in this file.
function StatusBar({ status, isAr, lang = isAr ? 'ar' : 'en' }) {
  const idx     = STATUS_STEPS.indexOf(status);
  const current = idx === -1 ? 0 : idx;
  const label   = requestStatusLabel(status, lang);
  return (
    <div style={{ margin: '12px 0 8px' }}>
      <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>
        {STATUS_STEPS.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 2,
            background: i <= current ? 'var(--text-tertiary)' : 'var(--border-subtle)',
            borderRadius: 1, transition: 'background 0.3s',
          }} />
        ))}
      </div>
      <p style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase' }}>
        {label}
      </p>
    </div>
  );
}

/* ─── Stat Card ──────────────────────────── */
function StatCard({ label, value, onClick, highlight }) {
  return (
    <div onClick={onClick} style={{
      background: highlight ? 'var(--bg-raised)' : 'var(--bg-subtle)',
      border: `1px solid ${highlight ? 'var(--border-muted)' : 'var(--border-subtle)'}`,
      padding: '24px 28px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderRadius: 'var(--radius-lg)',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = highlight ? 'var(--bg-raised)' : 'var(--bg-subtle)'}>
      <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 16, fontWeight: 500 }}>
        {label}
      </p>
      <p style={{ fontSize: 44, fontWeight: 300, color: highlight ? 'var(--text-primary)' : 'var(--text-secondary)', lineHeight: 1, letterSpacing: -1.5, fontVariantNumeric: 'lining-nums', fontFeatureSettings: '"lnum" 1' }}>
        {value}
      </p>
    </div>
  );
}

/* ─── relativeTime ───────────────────────── */
function relativeTime(dateStr, isAr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (isAr) {
    if (mins < 1)  return 'الآن';
    if (mins < 60) return `منذ ${mins} د`;
    if (hrs < 24)  return `منذ ${hrs} س`;
    if (days < 30) return `منذ ${days} يوم`;
    return new Date(dateStr).toLocaleDateString('ar-SA-u-nu-latn', { month: 'short', day: 'numeric' });
  }
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ─── StatusTimeline ─────────────────────── */
/* MOVED to src/lib/requestTimeline.js on 2026-09-22, unchanged.
   TIMELINE_STEPS, timelineIndexFromStatus and TimelineBar lived here and were
   not exported, so the admin console could not render the same bar without a
   third copy. The module is the trader's definition verbatim — same steps,
   same order, same status→index map — plus zh labels and the dimming of a
   cancelled request, both of which the owner asked for.
   ⚠️ The supplier's bar (DashboardSupplier.jsx:2775) is a DIFFERENT bar, not
   this one reworded. See the module header before assuming otherwise. */

/* Managed requests no longer use a buyer-facing sourcing timeline here — the full
   lifecycle lives in the dedicated tracker at /managed-order/:id. */

/* ─── PaymentBadge ───────────────────────── */
function PaymentBadge({ label, amount, currency, badgeState, isAr }) {
  const colors = {
    paid:    { bg: 'rgba(45,122,79,0.07)', border: 'rgba(45,122,79,0.3)',  text: 'var(--green)' },
    due:     { bg: 'rgba(160,136,80,0.07)', border: 'rgba(160,136,80,0.3)',  text: 'var(--warn)' },
    pending: { bg: 'var(--bg-subtle)',       border: 'var(--border-subtle)',   text: 'var(--text-disabled)' },
  };
  const c = colors[badgeState] || colors.pending;
  return (
    <div style={{ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${c.border}`, background: c.bg, minWidth: 104 }}>
      <p style={{ fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: c.text, marginBottom: 4, fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: 16, fontWeight: 300, color: c.text, lineHeight: 1, fontVariantNumeric: 'lining-nums', fontFeatureSettings: '"lnum" 1' }}>
        {amount > 0 ? formatSar(amount) : '—'}
        <span style={{ fontSize: 9, marginInlineStart: 3, color: 'var(--text-disabled)' }}>{currency}</span>
      </p>
      {badgeState === 'paid'    && <p style={{ fontSize: 9, color: c.text, marginTop: 3 }}>✓ {isAr ? 'مدفوع' : 'Paid'}</p>}
      {badgeState === 'due'     && <p style={{ fontSize: 9, color: c.text, marginTop: 3 }}>{isAr ? 'مطلوبة الآن' : 'Due now'}</p>}
      {badgeState === 'pending' && <p style={{ fontSize: 9, color: 'var(--text-disabled)', marginTop: 3 }}>{isAr ? 'بعد الشحن' : 'After shipping'}</p>}
    </div>
  );
}

/* ─── PaymentPlanRow ─────────────────────── */
function PaymentPlanRow({ request, offer, isAr, lang }) {
  if (!offer) return null;
  const showFrom = ['closed','supplier_confirmed','paid','ready_to_ship','shipping','arrived','delivered'];
  if (!showFrom.includes(request.status)) return null;

  // Plan item 7ج (2026-09-28): the split comes from lib/paymentProgress —
  // what was PAID first, then the trader's plan, then the old 30 default. It
  // used to be `payment_pct > 0 ? payment_pct : 30` alone, and payment_pct
  // was never written on the request, so a full payment read as a deposit.
  const { pct, full } = paymentSplit(request);
  // SAR only (owner, 2026-09-29), and the SAME figures as the Pay buttons:
  // payment_quote(), loaded with the request as request.quote. These badges
  // used to show the offer's own currency without the 5% fee, rounded to a
  // whole number — a second figure for the same payment. No quote → dashes.
  const q = request.quote?.payable ? request.quote : null;
  const totalSar = q ? Number(q.total_sar) : 0;
  const depositSar = q ? Number(q.first_paid_sar ?? q.deposit_sar) : 0;
  const firstAmt  = full ? totalSar : depositSar;
  const secondAmt = q ? Number(q.balance_sar ?? Math.max(0, Math.round((totalSar - depositSar) * 100) / 100)) : 0;
  const currency  = isAr ? 'ر.س' : 'SAR';

  const isPaidFirst  = ['paid','ready_to_ship','shipping','arrived','delivered'].includes(request.status);
  const isPaidSecond = ['shipping','arrived','delivered'].includes(request.status) || !!request.payment_second_paid;
  const isDueSecond  = request.status === 'ready_to_ship';

  // A full payment has ONE instalment. Paid → "Paid in full"; before payment
  // → "Full payment · 100%". Never a second badge, never "remainder due".
  if (full) {
    return (
      <div style={{ display: 'flex', gap: 8, margin: '10px 0', flexWrap: 'wrap' }}>
        <PaymentBadge
          label={isPaidFirst ? paidInFullLabel(lang) : `${fullPaymentLabel(lang)} · 100%`}
          amount={firstAmt}
          currency={currency}
          badgeState={isPaidFirst ? 'paid' : 'pending'}
          isAr={isAr}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, margin: '10px 0', flexWrap: 'wrap' }}>
      <PaymentBadge
        label={isAr ? `دفعة أولى · ${pct}%` : `1st · ${pct}%`}
        amount={firstAmt}
        currency={currency}
        badgeState={isPaidFirst ? 'paid' : 'pending'}
        isAr={isAr}
      />
      <PaymentBadge
        label={isAr ? `دفعة ثانية · ${100 - pct}%` : `2nd · ${100 - pct}%`}
        amount={secondAmt}
        currency={currency}
        badgeState={isPaidSecond ? 'paid' : isDueSecond ? 'due' : 'pending'}
        isAr={isAr}
      />
    </div>
  );
}

/* ─── TrackingCard ───────────────────────── */
/* WHO DELIVERS THIS TO ME — the row under the supplier's name.
   MOVED AND REWRITTEN 2026-09-21 (owner). It used to sit under the PRICE and
   read «أساس السعر: EXW» with a short gloss. Two problems with that:

     · it was filed as a property of the price, when what the trader is
       actually deciding is who brings the goods and how far; and
     · «EXW» is the supplier's vocabulary. A Saudi trader reads the number as
       the whole cost, so an EXW quote looked cheaper than a CIF one that
       included freight.

   So it is now a plain sentence, in Saudi register, directly under the name
   of the company making the promise — «يوصّلها لين بابك في الرياض». The
   Incoterm is not shown to the trader at all; the ⓘ still opens the full
   explanation for anyone who wants it.

   THE FLAG is the second half. When the offer stops SHORT of what the trader
   asked for on their own request, that is said plainly — and the sentence
   names what they asked for, not what they got, because "you asked for X and
   this is not X" is the useful form. Raised only for 'short'; never for
   'exceeds', because a supplier who goes further has done nothing wrong and
   an alarm there teaches the trader to ignore the one that matters.

   The comparison itself lives in src/lib/deliveryMatch.js — ONE predicate,
   one place. This component decides only how to say the answer.

   Copy: src/lib/priceBasisExplainer.js, owner-approved 2026-09-21. It is
   deliberately NOT the supplier wording in quoteStrings.js: that speaks to
   the factory in the second person about the same agreement. Two sides, one
   deal, two texts. */
function DeliveryRow({ basis, request, lang, isAr }) {
  const [open, setOpen] = useState(false);
  const info = explainPriceBasis(basis, lang);
  const line = deliveryLine(basis, lang, { city: request?.ship_city });
  const verdict = compareDelivery(basis, request?.delivery_preference);
  const flag = verdict === 'short' ? deliveryMismatchText(request?.delivery_preference, lang) : '';
  if (!line) return null;

  // Neutral for the statement, attention for the flag — and ONLY for the
  // flag. Offers predating price_basis (2026-09-19) are not at fault and a
  // warning colour on the commonest case reads as an accusation.
  const tone = basis ? 'var(--text-secondary)' : 'var(--text-disabled)';

  return (
    <div style={{ marginTop: 6 }}>
      <p style={{ fontSize: 12, color: tone, lineHeight: 1.6, margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        {line}
        {info?.full && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={isAr ? 'ما معنى هذا؟' : lang === 'zh' ? '这是什么意思？' : 'What does this mean?'}
            /* marginInlineStart, not marginLeft: this card renders under
               dir="rtl" for Arabic and the affordance must sit after the text
               in both directions. */
            style={{
              marginInlineStart: 6, padding: 0, border: 'none', background: 'none',
              color: 'var(--text-disabled)', cursor: 'pointer', fontSize: 12, lineHeight: 1,
            }}
          >
            ⓘ
          </button>
        )}
      </p>
      {open && info?.full && (
        <p style={{ fontSize: 11.5, color: 'var(--text-disabled)', lineHeight: 1.6, margin: '4px 0 0', maxWidth: 420, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
          {info.full}
        </p>
      )}
      {flag && (
        <p style={{
          fontSize: 11.5, lineHeight: 1.6, margin: '6px 0 0', padding: '6px 9px',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.22)',
          color: 'var(--red)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
        }}>
          {flag}
        </p>
      )}
    </div>
  );
}

function TrackingCard({ request, isAr }) {
  if (!request.tracking_number) return null;
  const trackUrl = (() => {
    const n = request.tracking_number;
    const urls = { DHL: `https://www.dhl.com/track?tracking-id=${n}`, FedEx: `https://www.fedex.com/tracking?tracknumbers=${n}`, Aramex: `https://www.aramex.com/track/${n}`, UPS: `https://www.ups.com/track?tracknum=${n}`, SMSA: `https://www.smsaexpress.com/track?awbno=${n}` };
    return urls[request.shipping_company] || `https://t.17track.net/en#nums=${n}`;
  })();
  return (
    <div style={{ margin: '10px 0', padding: '10px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {request.shipping_company && (
            <span style={{ fontSize: 10, color: 'var(--text-disabled)', letterSpacing: 0.5, display: 'block', marginBottom: 2 }}>{request.shipping_company}</span>
          )}
          <span style={{ fontSize: 13, color: 'var(--text-primary)', fontVariantNumeric: 'lining-nums', fontFeatureSettings: '"lnum" 1' }}>
            {request.tracking_number}
          </span>
          {request.estimated_delivery && (
            <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 3 }}>
              {isAr ? 'وصول متوقع: ' : 'ETA: '}
              {new Date(request.estimated_delivery).toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : 'en-US', { month: 'short', day: 'numeric' })}
            </p>
          )}
        </div>
        <a href={trackUrl} target="_blank" rel="noreferrer"
          style={{ fontSize: 10, color: 'var(--text-secondary)', letterSpacing: 1.5, textTransform: 'uppercase', textDecoration: 'none', border: '1px solid var(--border-subtle)', padding: '5px 10px', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>
          {isAr ? 'تتبع ←' : 'Track →'}
        </a>
      </div>
    </div>
  );
}

/* ─── TopSubTabs ─────────────────────────── */
function TopSubTabs({ tabs, active, onSelect, isAr }) {
  return (
    <div className="db-mobile-subtabs" style={{ gap: 6, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--border-subtle)' }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onSelect(t.id)} style={{
          padding: '5px 13px', borderRadius: 'var(--radius-chip)',
          border: `1px solid ${active === t.id ? 'var(--text-primary)' : 'var(--border-subtle)'}`,
          background: active === t.id ? 'var(--text-primary)' : 'none',
          color: active === t.id ? 'var(--bg-base)' : 'var(--text-secondary)',
          fontSize: 12, cursor: 'pointer',
          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
          transition: 'all 0.15s',
        }}>
          {t.label}
          {t.badge != null && t.badge > 0 && (
            <span style={{ marginInlineStart: 5, fontSize: 10, opacity: 0.75 }}>{t.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── MobileBottomNav ────────────────────── */
function MobileBottomNav({ activeTab, setActiveTab, nav, isAr, stats, moreOpen, setMoreOpen }) {
  const moreActive = ['direct-orders','samples','product-inquiries','settings'].includes(activeTab);
  const items = [
    { id: 'overview',  label: isAr ? 'الرئيسية' : 'Home'     },
    { id: 'requests',  label: isAr ? 'طلباتي'   : 'Requests' },
    { id: 'new',       label: isAr ? 'اطلب'     : 'New RFQ'  },
    { id: 'messages',  label: isAr ? 'الرسائل'  : 'Messages', badge: stats.messages > 0 ? stats.messages : null },
    { id: 'more',      label: isAr ? 'المزيد'   : 'More' },
  ];
  return (
    <nav className="db-bottom-nav" dir={isAr ? 'rtl' : 'ltr'}>
      {moreOpen && (
        <div onClick={() => setMoreOpen(false)} style={{
          position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.3)',
        }} />
      )}
      {moreOpen && (
        <div style={{
          position: 'fixed', bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          left: 0, right: 0, zIndex: 199,
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border-muted)',
          borderBottom: 'none',
          borderRadius: '16px 16px 0 0',
          padding: '16px 20px 8px',
          borderTop: '1px solid var(--border)',
          animation: 'slideUp 0.2s ease',
        }}>
          {[
            { id: 'direct-orders',     label: isAr ? 'مشترياتي المباشرة' : 'My Direct Purchases' },
            { id: 'samples',           label: isAr ? 'العينات' : 'Samples' },
            { id: 'product-inquiries', label: isAr ? 'استفسارات المنتجات' : 'Product Inquiries' },
            { id: 'settings',          label: isAr ? 'الإعدادات' : 'Settings' },
          ].map((item, i, arr) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); setMoreOpen(false); }}
              style={{
                display: 'block', width: '100%',
                textAlign: isAr ? 'right' : 'left',
                padding: '13px 0', background: 'none', border: 'none',
                borderBottom: i < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                color: activeTab === item.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: 15, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                cursor: 'pointer', fontWeight: activeTab === item.id ? 600 : 400,
              }}>
              {item.label}
            </button>
          ))}
        </div>
      )}
      {items.map(item => {
        const isActive = item.id === 'more' ? moreActive : item.id === 'new' ? false : activeTab === item.id;
        return (
          <button key={item.id}
            onClick={() => {
              if (item.id === 'new')  { nav('/requests'); return; }
              if (item.id === 'more') { setMoreOpen(o => !o); return; }
              setActiveTab(item.id);
              setMoreOpen(false);
            }}
            style={{
              flex: 1, padding: '10px 4px 6px',
              background: 'none', border: 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              cursor: 'pointer', position: 'relative',
              color: isActive ? 'var(--text-primary)' : 'var(--text-disabled)',
              transition: 'color 0.15s',
            }}>
            {item.badge && (
              <span style={{
                position: 'absolute', top: 6, insetInlineEnd: '18%',
                width: 14, height: 14, borderRadius: '50%',
                background: 'var(--red)', color: 'var(--on-dark)',
                fontSize: 8, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{item.badge > 9 ? '9+' : item.badge}</span>
            )}
            <span style={{
              fontSize: 10, letterSpacing: 0.5,
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              fontWeight: isActive ? 600 : 400,
            }}>
              {item.label}
            </span>
            {isActive && <div style={{ width: 16, height: 2, background: 'var(--text-primary)', borderRadius: 1, marginTop: 1 }} />}
          </button>
        );
      })}
    </nav>
  );
}

/* ─── Back Button ────────────────────────── */
function BackBtn({ onClick, isAr }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none',
      color: 'var(--text-disabled)', fontSize: 11,
      cursor: 'pointer', letterSpacing: 2,
      textTransform: 'uppercase',
      fontFamily: 'var(--font-sans)',
      padding: 0, marginBottom: 32,
      transition: 'color 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-disabled)'}>
      {isAr ? 'رجوع ←' : '← Back'}
    </button>
  );
}

/* ─── Main ───────────────────────────────── */
export default function DashboardBuyer({ user, profile, lang, displayCurrency, setDisplayCurrency, exchangeRates }) {
  // HioBuy-only mode sends "browse" to the marketplace (lib/marketMode.js).
  const { hioBuyOnly } = useHioBuyOnly();
  // A catalogue/direct-order price in SAR — the only currency a trader sees
  // (owner, 2026-09-29) — at the admin rate (exchangeRates.SAR, the rate an
  // offer locks on acceptance). An amount with no rate (CNY) is a dash.
  const toSarText = (amount, sourceCurrency) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return '—';
    const src = String(sourceCurrency || 'USD').toUpperCase();
    if (src !== 'SAR' && src !== 'USD') return '—';
    const sar = src === 'SAR' ? n : n * Number(exchangeRates?.SAR || 0);
    return sar > 0 ? `${formatSar(sar)} ${isAr ? 'ر.س' : 'SAR'}` : '—';
  };
  const nav      = useNavigate();
  const location = useLocation();
  const isAr     = lang === 'ar';

  // Handle dashboard query params (tab focus). The idea-flow resume
  // (?ai_report_resume=1) is handled by the global AIHub widget itself.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    // The messages tab is gone: it duplicated /inbox and did it worse, reading
    // only `.eq('receiver_id', me)` and so never seeing a buyer->factory message
    // (those carry receiver_id NULL — factoryChat.js:5-7). Old links, bookmarks
    // and notification targets still arrive here, so they are sent on rather
    // than landing on a tab that no longer exists.
    if (tab === 'messages') { nav('/inbox', { replace: true }); return; }
    if (tab) setActiveTab(tab);
  }, [location.search, nav]);

  const [stats, setStats]                 = useState({ requests: 0, messages: 0, offers: 0, productInquiries: 0 });
  const [myRequests, setMyRequests]       = useState([]);
  // requestId -> how many offers are held back pending supplier approval.
  const [pendingReview, setPendingReview] = useState({});
  const [productInquiries, setProductInquiries] = useState([]);
  const [activeTab, setActiveTab]         = useState('overview');
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [samples, setSamples] = useState([]);
  const [directOrders, setDirectOrders] = useState([]);
  const [loadingDirectOrders, setLoadingDirectOrders] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);   // { requestId, order } for the invoice modal
  const [directOrderPaying, setDirectOrderPaying] = useState({});
  const [directOrderActioning, setDirectOrderActioning] = useState({});
  const focusedRequestId = new URLSearchParams(location.search).get('request');

  // Review modal
  const [reviewModal, setReviewModal]           = useState(null);
  const [reviewRating, setReviewRating]         = useState(0);
  const [reviewComment, setReviewComment]       = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittedReviewIds, setSubmittedReviewIds] = useState(new Set());

  // Edit/Delete request
  const [editReqModal, setEditReqModal]   = useState(null);
  const [editReqForm, setEditReqForm]     = useState({});
  const [savingEditReq, setSavingEditReq] = useState(false);
  const [editReqError, setEditReqError]   = useState('');

  // Cancel request confirmation
  const [cancelConfirmReq, setCancelConfirmReq] = useState(null);

  // Sub-filters (mobile)
  const [reqSubFilter, setReqSubFilter] = useState('all');
  const [reqTypeFilter, setReqTypeFilter] = useState('all'); // all | factory | managed | idea | direct
  const [expandedReq, setExpandedReq] = useState(null);      // request id whose card is expanded

  // Settings
  const [settings, setSettings]         = useState({ full_name: '', phone: '', city: '', company_name: '', preferred_display_currency: displayCurrency || 'USD' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    if (!user) { nav('/login/buyer'); return; }
    loadStats();
    loadMyDirectOrders();

    // Realtime — refresh when offers/requests change
    const channel = sb.channel(`buyer-dash-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'offers' }, () => {
        loadStats();
        if (activeTab === 'requests') loadMyRequests();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'requests', filter: `buyer_id=eq.${user.id}` }, () => {
        loadStats(); loadMyDirectOrders();
        if (activeTab === 'requests') loadMyRequests();
      })
      .subscribe();
    return () => sb.removeChannel(channel);
  }, [user]);

  useEffect(() => {
    if (activeTab === 'requests') loadMyRequests();
    if (activeTab === 'direct-orders') loadMyDirectOrders();
    if (activeTab === 'product-inquiries') loadProductInquiries();
    if (activeTab === 'samples') loadMySamples();
    if (activeTab === 'settings') loadSettings();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'requests' || !focusedRequestId || loadingRequests) return;
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-request-id="${String(focusedRequestId)}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [activeTab, focusedRequestId, loadingRequests, myRequests.length]);

  useEffect(() => {
    setSettings(prev => ({ ...prev, preferred_display_currency: displayCurrency || 'USD' }));
  }, [displayCurrency]);

  const loadStats = async () => {
    const { data: myReqIds } = await sb.from('requests').select('id').eq('buyer_id', user.id);
    const ids = myReqIds?.map(r => r.id) || [];
    const [requests, messages, offers, productInquiries] = await Promise.all([
      sb.from('requests').select('id', { count: 'exact' }).eq('buyer_id', user.id),
      sb.from('messages').select('id', { count: 'exact' }).eq('receiver_id', user.id).eq('is_read', false),
      ids.length > 0
        ? sb.from('offers').select('id', { count: 'exact' }).eq('status', 'pending').in('request_id', ids).eq('managed_visibility', 'buyer_visible')
        : Promise.resolve({ count: 0 }),
      sb.from('product_inquiries').select('id', { count: 'exact' }).eq('buyer_id', user.id),
    ]);
    setStats({ requests: requests.count || 0, messages: messages.count || 0, offers: offers.count || 0, productInquiries: productInquiries.count || 0 });
  };

  const loadMyRequests = async () => {
    const key = `buyer-requests:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setMyRequests(cached); else setLoadingRequests(true);
    // Exclude direct purchase orders — they live in the dedicated direct-orders tab.
    const { data } = await sb.from('requests').select('*').eq('buyer_id', user.id).is('product_ref', null).order('created_at', { ascending: false });
    if (data) {
      const requestIds = data.map((request) => request.id);
      const managedRequestIds = data.filter((request) => isManagedRequest(request)).map((request) => request.id);

      const briefsResult = managedRequestIds.length > 0
        ? await sb.from('managed_request_briefs').select('*').in('request_id', managedRequestIds)
        : { data: [] };
      const briefByRequest = (briefsResult.data || []).reduce((acc, brief) => ({ ...acc, [brief.request_id]: brief }), {});

      // Fetch order_line_items for variant-based requests.
      // The FK column is `order_id` (referencing requests.id), not `request_id`.
      const { data: allLineItems } = requestIds.length
        ? await sb.from('order_line_items').select('*, product_variants(sku)').in('order_id', requestIds)
        : { data: [] };
      const linesByRequest = (allLineItems || []).reduce((acc, li) => {
        acc[li.order_id] = [...(acc[li.order_id] || []), li];
        return acc;
      }, {});

      // Batch: ONE offers query for every request + ONE profile attach for every
      // offer — instead of an N+1 storm (a separate offers + profiles round-trip
      // per request, which froze the tab on buyers with many requests).
      // buyer_visible ONLY — the `.is.null` fallback that used to sit here meant
      // "unmarked = show it", which is the column default, so an unapproved
      // supplier's quote would have been visible. Since 20260918172830 the
      // offers_select policy enforces the same thing server-side; this filter is
      // now a readability aid, not the guard.
      const { data: allOffers } = requestIds.length
        ? await sb.from('offers').select('*').in('request_id', requestIds).eq('managed_visibility', 'buyer_visible')
        : { data: [] };
      const offersWithProfilesRaw = await attachSupplierProfiles(sb, allOffers || [], 'supplier_id', 'profiles');
      // Every offer in SAR — the only currency a trader sees (owner, 2026-09-29:
      // the rate and the supplier's own figure are internal). One RPC for the
      // whole list: offer_amounts_sar() prices a pending offer at today's rate
      // and an accepted one at its locked rate, with the arithmetic
      // payment_quote() charges from. `o.sar` null = no amount to show.
      const sarByOffer = await fetchOfferAmounts(sb, (offersWithProfilesRaw || []).map((o) => o.id));
      const offersWithProfiles = (offersWithProfilesRaw || []).map((o) => ({ ...o, sar: sarByOffer[o.id] || null }));
      const offersByRequest = (offersWithProfiles || []).reduce((acc, offer) => {
        (acc[offer.request_id] = acc[offer.request_id] || []).push(offer);
        return acc;
      }, {});

      // What the trader owes, in SAR, for every request with an accepted offer —
      // from payment_quote(), the SAME call telr-create charges from, so the card
      // and the Pay buttons show Telr's figure to the halala (goods at the rate
      // locked when the offer was accepted, the 5% fee, the 30% deposit, and the
      // balance against the deposit actually recorded). One call per accepted
      // offer — a handful per trader. A failed call leaves the quote null and
      // the card shows no Pay button rather than a guess.
      const acceptedIds = requestIds.filter((rid) =>
        (offersByRequest[rid] || []).some((o) => String(o.status || '').toLowerCase() === 'accepted'));
      const quoteByRequest = {};
      await Promise.all(acceptedIds.map(async (rid) => {
        quoteByRequest[rid] = await fetchPaymentQuote(sb, { requestId: rid });
      }));

      // The request fee (299 SAR) on managed / team-search requests — the
      // trader's own rows only (RLS). One query for the whole list.
      const feeByRequest = await fetchRequestFees(sb, requestIds);

      // Offers still gated because the SUPPLIER is unverified. The page cannot
      // count these itself — offers_select hides them from the buyer on
      // purpose — so a SECURITY DEFINER function returns a bare number, never
      // an id, a name or a price. It excludes managed_raw: that gate never
      // lifts for the trader, and announcing it would promise a quote that
      // never arrives.
      const pendingReviewByRequest = {};
      await Promise.all((requestIds || []).map(async (rid) => {
        const { data: n, error: cErr } = await sb.rpc('get_request_pending_review_count', { p_request_id: rid });
        if (cErr) {
          console.error('[DashboardBuyer] pending-review count failed:', cErr.code || '', cErr.message || cErr);
          return;
        }
        if (Number(n) > 0) pendingReviewByRequest[rid] = Number(n);
      }));
      setPendingReview(pendingReviewByRequest);
      const withOffers = data.map((request) => ({
        ...request,
        offers: offersByRequest[request.id] || [],
        quote: quoteByRequest[request.id] || null,
        fee: feeByRequest[request.id] || null,
        brief: briefByRequest[request.id] || null,
        lineItems: linesByRequest[request.id] || [],
        // ═══ ADDED 2026-09-20 ═══════════════════════════════════════════════
        // `cancelled` is resolved HERE, in the data layer, off the RAW
        // requests.status — never recomputed in a card. Same contract the
        // admin console took after DistributePanel greyed itself on live
        // requests: a component fed from more than one source does not get to
        // guess which one it received.
        //
        // The predicate is isRequestCancelled from src/lib/requestStatus.js,
        // the single definition. Do not write a second one here: that is how
        // `cancelled` was fixed on the trader's list and nowhere else, which
        // is why the Home feed stayed broken for a day.
        //
        // What it gates: Accept / Reject / Edit. A trader accepted an offer on
        // a request they had CANCELLED and revived it into a live order
        // (2026-09-20). The DB refuses it now — policy
        // offers_accept_only_when_offerable and trigger trg_requests_no_revive,
        // migration 20260920210000 — and this is so the button is never
        // offered in the first place. RLS is the guard; this is the manners.
        requestCancelled: isRequestCancelled(request.status),
      }));
      writeSwrCache(key, withOffers);
      setMyRequests(withOffers);
    }
    setLoadingRequests(false);
  };

  const loadMyDirectOrders = async () => {
    const key = `buyer-direct-orders:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setDirectOrders(cached); else setLoadingDirectOrders(true);

    const ordersRes = await sb
      .from('requests')
      .select('*')
      .eq('buyer_id', user.id)
      .not('product_ref', 'is', null)
      .order('created_at', { ascending: false });
    console.log('[loadMyDirectOrders] requests query response:', ordersRes);

    const refIds = [...new Set((ordersRes.data || []).map(r => r.product_ref).filter(Boolean))];
    if (refIds.length === 0) {
      writeSwrCache(key, []);
      setDirectOrders([]);
      setLoadingDirectOrders(false);
      return;
    }

    const productsRes = await sb
      .from('products')
      .select(`id, supplier_id, name_ar, name_en, name_zh, currency, spec_lead_time_days, gallery_images, image_url, incoterms, port_of_loading, lead_time_min_days, lead_time_max_days, lead_time_negotiable, country_of_origin, ${PRODUCT_TIER_EMBED}`)
      .in('id', refIds);
    console.log('[loadMyDirectOrders] products query response:', productsRes);

    const productsWithProfiles = await attachSupplierProfiles(sb, productsRes.data || [], 'supplier_id', 'profiles');
    console.log('[loadMyDirectOrders] products with supplier profiles:', productsWithProfiles);

    const productsById = (productsWithProfiles || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

    const directResult = (ordersRes.data || []).map(r => ({ ...r, product: productsById[r.product_ref] || null }));
    writeSwrCache(key, directResult);
    setDirectOrders(directResult);
    setLoadingDirectOrders(false);
  };

  const markDirectOrderArrived = async (request) => {
    if (!request?.id || !request?.product?.supplier_id) {
      alert(isAr ? 'تعذّر تنفيذ العملية' : 'Could not perform this action');
      return;
    }
    setDirectOrderActioning(prev => ({ ...prev, [request.id]: 'marking_arrived' }));

    const supplierId = request.product.supplier_id;
    // TRADER-facing: this one feeds the trilingual notification row below,
    // which renders on the trader's own dashboard. Arabic first is right.
    const productName = request.product?.name_ar || request.product?.name_en || request.product?.name_zh || request.title_ar || request.title_en || '';
    // SUPPLIER-facing: the direct_order_arrived EMAIL. English first, falling
    // back to the request title when the product row has no name at all.
    // Same defect class as the M-10087 offer_accepted subject line.
    const supplierProductName = supplierEmailProductName(request.product,
      supplierEmailTitle(request, productName));

    const updRes = await sb
      .from('requests')
      .update({ status: 'arrived', shipping_status: 'arrived' })
      .eq('id', request.id)
      .select()
      .single();
    console.log('[markDirectOrderArrived] update response:', updRes);
    if (updRes.error) {
      setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
      alert(isAr ? 'تعذّر التحديث — حاول مرة أخرى' : lang === 'zh' ? '无法更新 — 请重试' : 'Could not update — try again');
      return;
    }

    const notifRes = await sb.from('notifications').insert({
      user_id: supplierId,
      type: 'arrived',
      title_ar: `استلم التاجر الشحنة — ${productName}`,
      title_en: `Buyer marked shipment arrived — ${productName}`,
      title_zh: `买家已收到货物 — ${productName}`,
      ref_id: request.id,
      is_read: false,
    }).select().single();
    console.log('[markDirectOrderArrived] notification response:', notifRes);

    try {
      const r = await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'direct_order_arrived',
          data: {
            recipientUserId: supplierId,
            // English-first, not the trader's Arabic. No `lang` here for the
            // same reason as offer_accepted: the template defaults to 'en'.
            productName: supplierProductName,
            trackingNumber: request.tracking_number || '',
          },
        }),
      });
      const body = await r.json().catch(() => null);
      console.log('[markDirectOrderArrived] email response:', { status: r.status, body });
    } catch (emailError) {
      console.error('[markDirectOrderArrived] email error:', emailError);
    }

    setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
    loadMyDirectOrders();
  };

  const confirmDirectDelivery = async (request) => {
    if (!request?.id || !request?.product?.supplier_id) {
      alert(isAr ? 'تعذّر تنفيذ العملية' : 'Could not perform this action');
      return;
    }
    if (!window.confirm(isAr
      ? 'تأكيد استلام البضاعة؟ سيتم تحرير المبلغ للمورد ولا يمكن التراجع.'
      : lang === 'zh'
        ? '确认收货？款项将放给供应商，此操作不可撤销。'
        : 'Confirm delivery? This will release the payment to the supplier and cannot be undone.')) {
      return;
    }
    setDirectOrderActioning(prev => ({ ...prev, [request.id]: 'confirming_delivery' }));

    const supplierId = request.product.supplier_id;
    const supplierName = request.product?.profiles?.company_name || request.product?.profiles?.full_name || 'Supplier';
    const productName = request.product?.name_ar || request.product?.name_en || request.product?.name_zh || request.title_ar || request.title_en || '';

    const updRes = await sb
      .from('requests')
      .update({ status: 'delivered', shipping_status: 'delivered' })
      .eq('id', request.id)
      .select()
      .single();
    console.log('[confirmDirectDelivery] update response:', updRes);
    if (updRes.error) {
      setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
      alert(isAr ? 'تعذّر تأكيد التسليم — حاول مرة أخرى' : lang === 'zh' ? '无法确认收货 — 请重试' : 'Could not confirm delivery — try again');
      return;
    }

    const paymentLookupRes = await sb
      .from('payments')
      .select('id, amount')
      .eq('request_id', request.id)
      .eq('buyer_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    console.log('[confirmDirectDelivery] payments lookup response:', paymentLookupRes);

    let payoutAmount = 0;
    if (paymentLookupRes.data?.id) {
      payoutAmount = Number(paymentLookupRes.data.amount || 0);
      const payUpdRes = await sb
        .from('payments')
        .update({ status: 'completed' })
        .eq('id', paymentLookupRes.data.id)
        .select()
        .single();
      console.log('[confirmDirectDelivery] payments update response:', payUpdRes);
    }

    const notifRes = await sb.from('notifications').insert({
      user_id: supplierId,
      type: 'delivery_confirmed',
      title_ar: `أكد التاجر الاستلام — سيتم تحويل المبلغ خلال 24 ساعة (${productName})`,
      title_en: `Buyer confirmed delivery — payout within 24h (${productName})`,
      title_zh: `买家已确认收货 — 24 小时内放款 (${productName})`,
      ref_id: request.id,
      is_read: false,
    }).select().single();
    console.log('[confirmDirectDelivery] notification response:', notifRes);

    try {
      const r = await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'payout_initiated',
          data: {
            recipientUserId: supplierId,
            name: supplierName,
            amount: payoutAmount,
          },
        }),
      });
      const body = await r.json().catch(() => null);
      console.log('[confirmDirectDelivery] email response:', { status: r.status, body });
    } catch (emailError) {
      console.error('[confirmDirectDelivery] email error:', emailError);
    }

    setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
    loadMyDirectOrders();
  };

  // Pay an ACCEPTED OFFER via Telr — the same hosted page and stages the mobile
  // app uses (PaymentScreen.js): 'deposit' (30%) or 'full' (100%) first, then
  // 'balance'. telr-create prices it server-side from the accepted offer + the
  // visible 5% fee. Replaced the Moyasar /checkout route on 2026-09-26: Telr is
  // the only gateway (owner's decision), and the web was the last surface still
  // sending offer payments to Moyasar.
  const payOfferStage = async (requestId, stage) => {
    setDirectOrderPaying(prev => ({ ...prev, [requestId]: true }));
    try {
      await startTelrPayment(requestId, stage);   // redirects to Telr on success
    } catch (e) {
      console.error('[DashboardBuyer] could not start the offer payment:', e?.status || '', e?.message || e);
      setDirectOrderPaying(prev => ({ ...prev, [requestId]: false }));
      alert(paymentStartErrorAlert(e, lang));
    }
  };

  // Pay via Telr (hosted page): deposit 30% or full 100%. Creates the order
  // server-side (with the visible 5% Maabar fee) and redirects to Telr.
  const payDirectOrder = async (request, stage = 'full') => {
    if (!request?.id || !request?.product) {
      alert(isAr ? 'تعذّر تجهيز الدفع — حاول إعادة تحميل الصفحة' : 'Could not prepare payment — try refreshing the page');
      return;
    }
    if (request.status !== 'supplier_confirmed') {
      alert(isAr ? 'لا يمكن الدفع قبل تأكيد المورد' : 'Cannot pay before the supplier confirms');
      return;
    }
    setDirectOrderPaying(prev => ({ ...prev, [request.id]: true }));
    try {
      await startTelrPayment(request.id, stage);   // redirects to Telr on success
    } catch (e) {
      console.error('[DashboardBuyer] could not start the order payment:', e?.status || '', e?.message || e);
      setDirectOrderPaying(prev => ({ ...prev, [request.id]: false }));
      alert(paymentStartErrorAlert(e, lang));
    }
  };

  const loadMySamples = async () => {
    const key = `buyer-samples:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setSamples(cached);
    const { data } = await sb.from('samples')
      .select('*,products(name_ar,name_en,name_zh)')
      .eq('buyer_id', user.id)
      .order('created_at', { ascending: false });
    if (data) {
      const result = await attachSupplierProfiles(sb, data, 'supplier_id', 'profiles');
      writeSwrCache(key, result);
      setSamples(result);
    }
  };


  const loadProductInquiries = async () => {
    const key = `buyer-inquiries:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setProductInquiries(cached);
    try {
      const data = await fetchProductInquiryThreads(sb, { buyerId: user.id });
      const result = await attachSupplierProfiles(sb, data, 'supplier_id', 'profiles');
      writeSwrCache(key, result);
      setProductInquiries(result);
    } catch (error) {
      console.error('load buyer product inquiries error:', error);
      // keep cached/existing data on error instead of blanking the tab
    }
  };

  const loadSettings = async () => {
    const { data } = await sb.from('profiles').select('full_name,phone,city,company_name').eq('id', user.id).single();
    if (data) {
      setSettings({
        full_name: data.full_name || '',
        phone: data.phone || '',
        city: data.city || '',
        company_name: data.company_name || '',
        preferred_display_currency: displayCurrency || 'USD',
      });
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    await sb.from('profiles').update({
      full_name: settings.full_name, phone: settings.phone,
      city: settings.city, company_name: settings.company_name,
    }).eq('id', user.id);
    await setDisplayCurrency?.(settings.preferred_display_currency || 'USD');
    setSavingSettings(false);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 3000);
  };

  const rejectOffer = async (offerId, supplierId, requestId) => {
    if (!window.confirm(isAr ? 'هل تريد رفض هذا العرض؟' : 'Reject this offer?')) return;
    const { data: reqData } = await sb.from('requests').select('title_ar,title_en').eq('id', requestId).single();
    const reqTitle = reqData?.title_ar || reqData?.title_en || '';

    const { error } = await sb.from('offers').update({ status: 'rejected' }).eq('id', offerId);
    if (error) {
      alert(isAr ? 'حدث خطأ أثناء رفض العرض' : 'Error rejecting offer');
      return;
    }

    sb.from('notifications').insert({
      user_id: supplierId,
      type: 'offer_rejected',
      title_ar: `تم رفض عرضك على الطلب: ${reqTitle}`,
      title_en: `Your offer was rejected for: ${reqTitle}`,
      title_zh: `您的报价已被拒绝: ${reqTitle}`,
      ref_id: requestId,
      is_read: false,
    }).then(undefined, () => {});   // best-effort, background

    setMyRequests(prev => prev.map(req => req.id !== requestId ? req : {
      ...req,
      offers: req.offers.map(o => o.id === offerId ? { ...o, status: 'rejected' } : o),
    }));
    loadStats();
  };

  const acceptOffer = async (offerId, supplierId, requestId) => {
    // Get request title and all other pending offers before updating
    const { data: allOffers } = await sb.from('offers')
      .select('id,supplier_id')
      .eq('request_id', requestId)
      .eq('status', 'pending')
      .eq('managed_visibility', 'buyer_visible')
      .neq('id', offerId);
    // title_zh is selected for supplierEmailTitle's middle rung, not for display.
    const { data: reqData } = await sb.from('requests').select('title_ar,title_en,title_zh').eq('id', requestId).single();
    // TWO titles on purpose, and they are not interchangeable.
    // reqTitle is TRADER-facing: it feeds the in-app notification rows below,
    // which the trader's own dashboard renders. Arabic first is right there.
    const reqTitle = reqData?.title_ar || reqData?.title_en || '';
    // supplierTitle is SUPPLIER-facing and goes in the offer_accepted EMAIL.
    // Sending reqTitle here is the M-10087 defect: an English email whose
    // subject read «كفرات iPhone 15». See src/lib/supplierEmailContent.js.
    const supplierTitle = supplierEmailTitle(reqData, reqTitle);

    try {
      const { error: e1 } = await sb.from('offers').update({ status: 'accepted' }).eq('id', offerId);
      if (e1) { console.error('acceptOffer: failed to set offer accepted:', e1); alert(isAr ? 'حدث خطأ أثناء قبول العرض' : 'Error accepting offer'); return; }

      const { error: e2 } = await sb.from('requests').update({ status: 'closed' }).eq('id', requestId);
      if (e2) { console.error('acceptOffer: failed to close request:', e2); alert(isAr ? 'حدث خطأ أثناء تحديث الطلب' : 'Error updating request'); return; }

      const { error: e3 } = await sb.from('offers').update({ status: 'rejected' }).eq('request_id', requestId).neq('id', offerId);
      if (e3) { console.error('acceptOffer: failed to reject other offers:', e3); alert(isAr ? 'حدث خطأ أثناء تحديث العروض الأخرى' : 'Error updating other offers'); return; }
    } catch (e) {
      console.error('acceptOffer: unexpected DB error:', e);
      alert(isAr ? 'حدث خطأ غير متوقع' : 'Unexpected error');
      return;
    }

    // All DB writes succeeded. Notifications + emails (the accepted supplier AND
    // every rejected supplier) are best-effort — fire them in the background so
    // the buyer's UI refreshes immediately instead of waiting on several email
    // round-trips in sequence.
    (() => {
      sb.from('notifications').insert({
        user_id: supplierId, type: 'offer_accepted',
        title_ar: 'تم قبول عرضك', title_en: 'Your offer has been accepted', title_zh: '您的报价已被接受',
        ref_id: offerId, is_read: false,
      }).then(undefined, () => {});
      fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        // No `lang`: offer_accepted reads `d.lang || 'en'`, so omitting it IS
        // English — which is what a supplier-bound mail should be. Do not
        // "helpfully" pass the trader's lang here.
        body: JSON.stringify({ type: 'offer_accepted', data: { recipientUserId: supplierId, name: 'Supplier', requestTitle: supplierTitle } }),
      }).catch((e) => console.error('email error:', e));
      (allOffers || []).forEach((o) => {
        sb.from('notifications').insert({
          user_id: o.supplier_id, type: 'offer_rejected',
          title_ar: `تم اختيار عرض آخر على الطلب: ${reqTitle}`,
          title_en: `Another offer was selected for: ${reqTitle}`,
          title_zh: `已选择其他报价: ${reqTitle}`,
          ref_id: requestId, is_read: false,
        }).then(undefined, () => {});
        // ═══ REMOVED 2026-09-22 — the client-side offer_rejected EMAIL ══════
        // A fetch to send-email with type 'offer_rejected' sat here, firing
        // once per rejected offer, immediately.
        //
        // THE DATABASE OWNS THIS NOW and sends it IMMEDIATELY, from an AFTER
        // trigger on the write three lines above: trg_notify_request_closed /
        // notify_request_closed(), migration 20260922144335, template
        // 'offer_lost'. It replaces this loop rather than supplementing it —
        // running both would send two emails for one event.
        //
        // ⚠️ This comment described a 24h pg_cron dispatcher and a trigger
        // named trg_record_request_close until 2026-09-22. NEITHER EVER
        // EXISTED: the deferral was dropped before anything was applied. Left
        // as it was, it would have sent the next person looking for a cron job
        // that is not there (golden rule 11).
        //
        // It was replaced, not merely moved, because this loop could not do
        // the job:
        //   · IT MISSED THE SUPPLIERS WHO MOST NEEDED TELLING. It iterates
        //     `allOffers`, which came from a SELECT, and offers_select hides
        //     managed_visibility = 'admin_only' rows from the trader. Those
        //     suppliers were marked rejected by the update above — which DOES
        //     reach them, since offers_update_buyer keys on buyer_id and never
        //     mentions visibility — and then told nothing at all.
        //   · The offer_rejected template has no Arabic.
        //   · It ran in a browser tab, so closing the tab between the writes
        //     and the sends meant nobody was emailed.
        //
        // ⚠️ DO NOT RE-ADD A CLIENT EMAIL CALL HERE. The in-app notification
        // insert above STAYS: it is immediate by design and is a different
        // channel, not a duplicate of the mail.
        //
        // ⚠️ THE NOTIFICATION LOOP STILL HAS THE BUG THE EMAIL NO LONGER HAS.
        // `allOffers` is filtered to managed_visibility = 'buyer_visible', so
        // an admin_only supplier gets the EMAIL (the trigger reads every
        // visibility) and NO in-app notification. Unfixed here on purpose:
        // inserting notifications for rows the trader cannot see is a
        // notifications-RLS question, not a presentation change. Logged.
        // ═══════════════════════════════════════════════════════════════════
      });
    })();

    loadMyRequests();
  };

  const confirmDelivery = async (requestId, supplierId, supplierName) => {
    // Update request status
    await sb.from('requests').update({ status: 'delivered', shipping_status: 'delivered' }).eq('id', requestId);

    // Trigger payout: mark payment as completed
    const { data: paymentData } = await sb.from('payments')
      .select('id, amount, amount_second, payment_pct')
      .eq('request_id', requestId)
      .eq('buyer_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (paymentData) {
      // If split payment and second payment was made, total is fully paid
      // Mark payment as completed — triggers payout to supplier
      await sb.from('payments')
        .update({ status: 'completed' })
        .eq('id', paymentData.id);
    }

    // Notify supplier
    await sb.from('notifications').insert({
      user_id: supplierId, type: 'delivery_confirmed',
      title_ar: 'التاجر أكد الاستلام — سيتم تحويل المبلغ خلال 24 ساعة',
      title_en: 'Buyer confirmed delivery — payout will be processed within 24h',
      title_zh: '买家已确认收货 — 款项将在24小时内处理',
      ref_id: requestId, is_read: false,
    });

    // Send payout email to supplier
    try {
      await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'payout_initiated',
          data: {
            recipientUserId: supplierId,
            name: supplierName || 'Supplier',
            amount: paymentData?.amount || 0,
          },
        }),
      });
    } catch (e) { console.error('payout email error:', e); }

    loadMyRequests();
    setReviewRating(0); setReviewComment('');
    setReviewModal({ supplierId, requestId, supplierName });
  };

  const submitReview = async () => {
    if (!reviewRating || !reviewModal) return;
    const { data: existingReview } = await sb.from('reviews')
      .select('id')
      .eq('supplier_id', reviewModal.supplierId)
      .eq('reviewer_id', user.id)
      .eq('request_id', reviewModal.requestId)
      .maybeSingle();
    if (existingReview) {
      setReviewModal(null);
      return;
    }
    setSubmittingReview(true);
    await sb.from('reviews').insert({
      supplier_id: reviewModal.supplierId, reviewer_id: user.id,
      request_id: reviewModal.requestId, rating: reviewRating, comment: reviewComment || '',
    });
    // profiles.rating/reviews_count are guard-protected and not directly
    // updatable by a buyer under RLS. Recompute server-side via SECURITY
    // DEFINER RPC, which averages the authoritative reviews table.
    await sb.rpc('recalc_supplier_rating', { p_supplier_id: reviewModal.supplierId });
    setSubmittingReview(false);
    setSubmittedReviewIds(prev => new Set([...prev, reviewModal.requestId]));
    setReviewModal(null);
    alert(isAr ? 'شكراً على تقييمك!' : 'Thank you for your review!');
  };

  const saveEditRequest = async () => {
    if (!editReqModal) return;
    // REQUIRED, plain positive number only (owner, 2026-09-30): payment
    // multiplies price × quantity. This modal used to save whatever was typed,
    // including an empty box. It has no unit field, so the no-unit wording.
    // A legacy row whose quantity is text ("500 units") cannot be saved until
    // the trader rewrites it as a number — that is the point.
    const quantity = String(editReqForm.quantity ?? '').trim();
    if (!isPlainQuantity(quantity)) {
      setEditReqError(quantityMessage(lang, { hasUnitField: false }));
      return;
    }
    setEditReqError('');
    setSavingEditReq(true);
    await sb.from('requests').update({
      title_ar: editReqForm.title_ar,
      title_en: editReqForm.title_en,
      // requests has description_ar, not desc_ar — the old key failed the whole update.
      description_ar: editReqForm.desc_ar,
      quantity,
    }).eq('id', editReqModal.id);
    setSavingEditReq(false);
    setEditReqModal(null);
    loadMyRequests();
  };

  // ── The three stages a request can be in, for the trader ──────────────────
  //
  // Mirrors the RLS applied in migration 20260920120000. The policies are the
  // enforcement; this is what stops the UI offering an action the database
  // will refuse.
  //
  //   1  open, zero offers            -> Delete
  //   2  offers exist, none accepted  -> Cancel; the row and the quotes survive
  //   3  an offer accepted, or paid   -> NEITHER. No refund flow exists, so the
  //                                      trader talks to us and we handle it.
  //
  // Any offer counts at stage 2, not just pending ones: a rejected or held
  // quote is still a supplier's work and still the record of what happened.
  const PAID_OR_LATER = ['paid', 'ready_to_ship', 'shipping', 'arrived', 'delivered', 'completed'];

  const requestStage = (r) => {
    const offers = r.offers || [];
    if (offers.some(o => o.status === 'accepted') || PAID_OR_LATER.includes(r.status)) return 3;

    // A MANAGED REQUEST IS STAGE 2 FROM CREATION, offers or not. Work starts on
    // our side the moment it exists -- a brief is written, suppliers are
    // matched -- so it was never the trader's to delete, and it is not "open
    // with nothing on it" even when the offer count is zero.
    //
    // Before this, a managed request fell to stage 1 and was offered Delete
    // that the RLS then refused (it requires status = 'open', which a managed
    // row is not), while Cancel was withheld because the stage-2 test asked for
    // an offer to exist. Neither action worked: the trader was stuck with a
    // request they could not stop. Found in the browser, 2026-09-20.
    //
    // isCuratedRequest is the JS mirror of public.request_is_curated(uuid)
    // (migration 20260919220000) -- request_kind managed/idea_to_product, or
    // sourcing_mode managed. Reused rather than restated so this does not
    // become a fifth expression of one rule.
    if (isCuratedRequest(r)) return 2;

    if (offers.length > 0) return 2;
    return 1;
  };

  /**
   * May the trader still edit this request?
   *
   * ONE fact: has anyone quoted yet. Not the stage, and not the kind -- a
   * managed request is as editable as a direct one while its brief is still
   * being shaped, which is precisely the period before an offer exists.
   *
   * Once ANY offer exists this returns false, for the reason the three-stage
   * rule exists at all: a supplier priced against what they read, and editing
   * the brief underneath their quote invalidates it without telling anyone.
   * Any offer counts, including a rejected or an admin-held one.
   *
   * Closed, cancelled and paid-or-later are excluded as well -- there is
   * nothing left to shape there, and the RLS would refuse most of it anyway.
   */
  /* ═══ CHANGED 2026-09-20: two negative status tests became one positive ═══
     Was: !['closed','cancelled'].includes(status) && !PAID_OR_LATER.includes(status)

     Twelve statuses exist (requests_status_check) and that pair named five of
     them, so the other seven were editable by default — including any status
     invented later. The same shape that let `cancelled` become a to-do item on
     the Home feed and a live stage bar on the managed box, both on this date.

     isRfqCollecting names the states editing ACTS ON, from
     src/lib/requestStatus.js — the single definition, not a second one written
     here. Behaviour for every currently-known status is UNCHANGED: with zero
     offers a request is realistically only ever 'open', and the collecting set
     excludes closed, cancelled and all six PAID_OR_LATER states exactly as
     before. What changes is the default for status number thirteen: not
     editable, rather than editable because nobody remembered to exclude it.

     The offers test stays first and stays as it is: ANY offer, including a
     rejected or an admin-held one, means a supplier has priced this brief and
     editing it underneath them invalidates their work without telling anyone. */
  const canEditRequest = (r) => (r.offers || []).length === 0
    && isRfqCollecting(r.status);

  const deleteRequest = async (r) => {
    // Stage 1 only. The old version detected offers, warned about them, and
    // then deleted anyway -- destroying suppliers' quotes on a confirm dialog.
    // The RLS refuses it now; this is so the trader is never offered it.
    if (requestStage(r) !== 1 || r.status !== 'open') return;
    if (!window.confirm(isAr ? 'هل تريد حذف هذا الطلب؟' : 'Delete this request?')) return;

    const { error } = await sb.from('requests').delete().eq('id', r.id);
    if (error) {
      console.error('[DashboardBuyer] delete request failed:', error.code || '', error.message || error);
      alert(isAr ? 'تعذّر حذف الطلب.' : 'Could not delete this request.');
      return;
    }
    loadMyRequests(); loadStats();
  };

  const cancelRequest = async (r) => {
    // Stage 2 only. Stage 3 has no cancel button, and the RLS added in
    // 20260920120000 refuses a landing on 'cancelled' once an offer is
    // accepted -- so reaching here with one would fail the write anyway. The
    // guard is here so it fails as a no-op rather than half-applying.
    if (requestStage(r) !== 2) { setCancelConfirmReq(null); return; }

    // ⚠️ WRITES 'cancelled', NOT 'open'. This line used to reopen the request,
    // which put a quoted request back into the state where Delete is offered --
    // the route by which a trader could destroy suppliers' work. The RLS now
    // refuses 'open' while any offer exists, so the old value would fail here.
    const { error } = await sb.from('requests').update({ status: 'cancelled' }).eq('id', r.id);
    if (error) {
      // The CODE is the whole diagnosis and it must reach the person looking at
      // the screen, not only the console they are not in:
      //   42501  RLS refused the new row -- a policy question
      //   23514  a CHECK constraint rejected the value -- 'cancelled' is not in
      //          requests.status's allowed set
      //   P0001  a trigger raised -- read the message
      // "Could not cancel this request" on its own sent us looking at the
      // policy when the policy was fine.
      console.error('[DashboardBuyer] cancel request failed:',
        error.code || '', error.details || '', error.hint || '', error.message || error);
      alert(`${isAr ? 'تعذّر إلغاء الطلب.' : lang === 'zh' ? '无法取消此需求。' : 'Could not cancel this request.'}\n\n[${error.code || '?'}] ${error.message || ''}`);
      setCancelConfirmReq(null);
      return;
    }

    // Tell EVERY supplier who quoted, not only an accepted one. At stage 2
    // there is no accepted offer by definition, so the old code -- which
    // notified only the accepted supplier -- told nobody at all. The quotes
    // survive; the people who wrote them are owed the reason they stopped.
    const reqNameAr = r.title_ar || r.title_en || '';
    const reqNameEn = r.title_en || r.title_ar || '';
    const body = {
      ar: `قام التاجر بإلغاء الطلب: ${reqNameAr}`,
      en: `The trader has cancelled the request: ${reqNameEn}`,
      zh: `采购商已取消请求: ${reqNameEn}`,
    };
    const quoted = [...new Set((r.offers || [])
      .filter(o => o.status !== 'cancelled')
      .map(o => o.supplier_id)
      .filter(Boolean))];

    await Promise.all(quoted.flatMap(supplierId => ([
      sb.from('notifications').insert({
        user_id: supplierId,
        type: 'request_cancelled',
        title_ar: body.ar,
        title_en: body.en,
        title_zh: body.zh,
        ref_id: r.id,
        is_read: false,
      }),
      sb.from('messages').insert({
        sender_id: user.id,
        receiver_id: supplierId,
        content: JSON.stringify(body),
        message_type: 'system',
        is_read: false,
      }),
    ])));
    setCancelConfirmReq(null);
    // Optimistic state matches what was actually written: 'cancelled', and the
    // offers untouched. The old version rewrote an accepted offer to
    // 'rejected' locally, which can no longer happen -- stage 3 has no cancel.
    setMyRequests(prev => prev.map(req =>
      req.id !== r.id ? req : { ...req, status: 'cancelled' }
    ));
  };

  const name = profile?.full_name || user?.email?.split('@')[0];

  // Stage 3 -- an offer accepted, or money moved -- has NO action on the card.
  // Not delete, not cancel, and deliberately no support link either (owner,
  // 2026-09-20): a trader with a problem on a paid order already knows how to
  // reach us, and putting a support channel there dresses the absence of a
  // refund flow up as a feature. The card just shows the order.
  //
  // ⚠️ Do not add a control here that writes a status. It would promise a
  // reversal the product cannot perform, and the RLS in 20260920120000 refuses
  // the write anyway -- so it would fail silently and read as broken rather
  // than absent.

  const directOrdersPayableCount = directOrders.filter(r => r.status === 'supplier_confirmed').length;
  const tabs = [
    { id: 'overview',  label: isAr ? 'نظرة عامة' : lang === 'zh' ? '概览' : 'Overview' },
    // No Messages entry here any more (owner, 2026-10-20): «الرسائل» is in the
    // header on every page (Navbar / MarketBar / phone «المزيد»), and a tab
    // that only navigated away was the same place a second time on this screen.
    // Names come from lib/navItems.js, shared with every header.
    { id: 'requests',  label: navLabel('quotes', lang) },
    // «مشترياتي» is the marketplace's orders everywhere else, so this tab — the
    // trader's direct buys from a factory — says what it is instead.
    { id: 'direct-orders', label: isAr ? 'الشراء من المصانع' : lang === 'zh' ? '工厂直购' : 'Factory purchases', badge: directOrdersPayableCount > 0 ? directOrdersPayableCount : null },
    { id: 'samples',   label: isAr ? 'العينات'   : 'Samples' },
    { id: 'product-inquiries', label: isAr ? 'استفسارات المنتجات' : lang === 'zh' ? '产品咨询' : 'Product Inquiries', badge: stats.productInquiries > 0 ? stats.productInquiries : null },
    { id: 'settings',  label: isAr ? 'الإعدادات' : 'Settings' },
  ];

  // An unrecognised ?tab= used to leave the buyer on a dashboard with an empty
  // content area: the reader above accepts any string, and no render branch
  // matches it, so nothing appeared and nothing said why. Mirrors the guard
  // DashboardSupplier has — and lands on overview, never on a prompt.
  //
  // Entries carrying `to` are excluded: those are links (Messages -> /inbox),
  // they render no pane, so treating one as a known tab would reintroduce the
  // blank screen this exists to prevent.
  //
  // Declared here rather than beside the reader because the dependency array is
  // evaluated during render, and `tabs` is only initialised on the line above.
  //
  // A joined string, not an array: `tabs` is rebuilt every render, so depending
  // on it — or on an array derived from it — would re-run this on every render.
  // A primitive compares by value, so the effect fires only when the set of
  // pane tabs actually changes, and the dependencies stay complete.
  const paneTabKey = tabs.filter((t) => !t.to).map((t) => t.id).join(',');
  useEffect(() => {
    if (!paneTabKey.split(',').includes(activeTab)) setActiveTab('overview');
  }, [activeTab, paneTabKey]);

  /* ── shared section style ── */
  const section = { animation: 'fadeIn 0.35s ease' };

  return (
    <div className="dashboard-wrap">

      {/* ══════════════════════════════════════
          HEADER
      ══════════════════════════════════════ */}
      <div className="dash-header-pad" style={{
        background: 'var(--bg-subtle)',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        {/* Label */}
        <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 20, fontWeight: 500 }}>
          {isAr ? 'مَعبر · لوحة التاجر' : lang === 'zh' ? 'Maabar · 采购商面板' : 'Maabar · Trader Dashboard'}
        </p>

        {/* Name */}
        <h1 style={{
          fontSize: isAr ? 36 : 42,
          fontWeight: 300,
          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
          color: 'var(--text-primary)',
          letterSpacing: isAr ? 0 : -1,
          lineHeight: 1.2,
          marginBottom: 10,
        }}>
          {isAr ? `أهلاً، ${name}` : lang === 'zh' ? `您好，${name}` : `Welcome, ${name}`}
        </h1>
        <p style={{
          fontSize: 14, color: 'var(--text-tertiary)',
          marginBottom: 36, lineHeight: 1.7,
          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
          maxWidth: 420,
        }}>
          {isAr ? 'تابع طلباتك وعروضك ورسائلك من مكان واحد' : lang === 'zh' ? '在一个地方跟踪您的需求、报价和消息' : 'Track your requests, offers and messages in one place'}
        </p>

        {/* Tabs */}
        <div className="db-desktop-tabs" style={{ gap: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => (t.to ? nav(t.to) : setActiveTab(t.id))} style={{
              padding: '10px 20px',
              background: 'none', border: 'none',
              borderBottom: activeTab === t.id
                ? '2px solid var(--text-primary)'
                : '2px solid transparent',
              // Readable at a glance (Apple HIG: legible tab labels): was 11px,
              // uppercase, in the disabled grey — every tab looked switched off.
              color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === t.id ? 600 : 400,
              fontSize: 13.5, cursor: 'pointer',
              transition: 'all 0.2s', position: 'relative',
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              letterSpacing: 0,
              minHeight: 44,
            }}>
              {t.label}
              {t.badge && (
                <span style={{
                  position: 'absolute', top: 6,
                  right: isAr ? 'auto' : 2, left: isAr ? 2 : 'auto',
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--border-muted)',
                  color: 'var(--text-secondary)',
                  fontSize: 8, fontWeight: 700, borderRadius: '50%',
                  width: 14, height: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════
          CONTENT
      ══════════════════════════════════════ */}
      <div className="db-main-wrap" style={{ background: 'var(--bg-base)', minHeight: 'calc(var(--app-dvh) - 280px)' }}>
        <div className="dash-content">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div style={section}>
              <BuyerHomePanel
                sb={sb}
                buyerId={user.id}
                lang={lang}
                name={name}
                messagesCount={stats.messages}
                onOpenRequests={() => setActiveTab('requests')}
                onOpenProducts={() => nav(hioBuyOnly ? '/market' : '/products')}
                onOpenSuppliers={hioBuyOnly ? null : () => nav('/factories')}
                onNewManaged={() => nav('/request')}
                onNewRfq={() => nav('/requests')}
                onNewIdea={() => window.dispatchEvent(new CustomEvent('maabar:open-idea'))}
                onOpenManaged={(id) => nav('/managed-order/' + id)}
                onOpenMessages={() => nav('/inbox')}
                showHeader={false}
              />
            </div>
          )}

          {/* ── MY REQUESTS ── */}
          {activeTab === 'requests' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} isAr={isAr} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
                <h2 style={{
                  fontSize: isAr ? 28 : 34, fontWeight: 300,
                  fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                  color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -0.5,
                }}>
                  {isAr ? 'طلباتي' : 'My Requests'}
                </h2>
                <button className="btn-dark-sm" onClick={() => nav('/requests')}
                  style={{ fontSize: 11, letterSpacing: 1, minHeight: 36 }}>
                  {isAr ? '+ طلب جديد' : '+ New Request'}
                </button>
              </div>

              {/* Payout assurance — ONCE, at the top of the requests section,
                  ALWAYS: no offer, request or loading condition. Reassurance has
                  to land before the trader decides, and the trader with no
                  offers yet (or no requests yet) is the one who needs it most.
                  The lead is bold --text-primary, NOT green: on this dashboard
                  green already means "paid", the lowest-price chip and the
                  confirm-delivery actions.
                  ⚠️ OPERATOR RULE, NOT A SYSTEM GATE. True because the team
                  does not transfer to a supplier it has not reviewed and
                  approved — nothing in the code blocks a transfer today: no
                  payout path exists (money-out is off-platform),
                  assert_supplier_payout_eligible has no caller, and "approved"
                  (is_verified_supplier) is a document review only. See
                  docs/TECH_DEBT.md, "Payout is not gated in code". Copy lives
                  in dashboardBuyerStrings.js — never add "on site" there. */}
              <p data-testid="payout-assurance" style={{
                fontSize: 13, lineHeight: 1.8, color: 'var(--text-secondary)',
                margin: '-8px 0 18px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                <strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{payoutAssurance(lang).lead}</strong>
                {' '}{payoutAssurance(lang).rest}
              </p>

              {/* Direct purchases — a clear, visible entry (not buried in "More"). */}
              {directOrders.length > 0 && (
                <button onClick={() => setActiveTab('direct-orders')}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 10, textAlign: isAr ? 'right' : 'left',
                    background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)',
                    [isAr ? 'borderRight' : 'borderLeft']: `3px solid ${directOrdersPayableCount > 0 ? 'var(--red, #C0503F)' : 'var(--bronze, #8B7355)'}`,
                    borderRadius: 'var(--radius-lg)', padding: '13px 15px', marginBottom: 16, cursor: 'pointer' }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {isAr ? 'مشترياتي المباشرة' : 'My Direct Purchases'}
                    </span>
                    {directOrdersPayableCount > 0 && (
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--red, #C0503F)', fontWeight: 600, marginTop: 2, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {directOrdersPayableCount} {isAr ? 'بانتظار الدفع' : 'awaiting payment'}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {directOrders.length} <span style={{ color: 'var(--text-disabled)' }}>{isAr ? '· عرض ←' : '· view →'}</span>
                  </span>
                </button>
              )}

              {/* Stats mini-strip */}
              {!loadingRequests && myRequests.length > 0 && (() => {
                const needsAct = myRequests.filter(r => {
                  // A cancelled request needs nothing from anyone. Without this
                  // one carrying pending offers still counted here, so the
                  // trader was told to act on a row that has no buttons.
                  if (r.status === CANCELLED) return false;
                  const hasPendingOffers = r.offers?.some(o => o.status === 'pending');
                  return hasPendingOffers || ['supplier_confirmed','arrived'].includes(r.status);
                }).length;
                const activeCount    = myRequests.filter(r => !['open','offers_received','delivered', CANCELLED].includes(r.status)).length;
                const completedCount = myRequests.filter(r => r.status === 'delivered').length;
                return (
                  <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                    {[
                      { label: isAr ? 'تحتاج إجراء' : 'Action needed', value: needsAct, red: true },
                      { label: isAr ? 'نشطة' : 'Active', value: activeCount },
                      { label: isAr ? 'مكتملة' : 'Completed', value: completedCount },
                    ].map((s, i) => (
                      <div key={i} style={{ flex: 1, padding: '12px 14px', background: 'var(--bg-subtle)', borderRight: i < 2 ? '1px solid var(--border-subtle)' : 'none' }}>
                        <p style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 5, fontWeight: 500 }}>{s.label}</p>
                        <p style={{ fontSize: 24, fontWeight: 300, lineHeight: 1, fontVariantNumeric: 'lining-nums', fontFeatureSettings: '"lnum" 1', color: s.red && s.value > 0 ? 'var(--red)' : 'var(--text-secondary)' }}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Mobile sub-filter tabs */}
              <TopSubTabs
                isAr={isAr}
                active={reqSubFilter}
                onSelect={setReqSubFilter}
                tabs={[
                  { id: 'all',       label: isAr ? 'الكل'    : 'All'       },
                  { id: 'open',      label: isAr ? 'مفتوحة'  : 'Open',     badge: myRequests.filter(r => ['open','offers_received'].includes(r.status)).length || null },
                  { id: 'active',    label: isAr ? 'نشطة'    : 'Active'    },
                  { id: 'completed', label: isAr ? 'مكتملة'  : 'Done'      },
                ]}
              />

              {/* Type filter — factory quote / managed / idea / direct */}
              {myRequests.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' }}>
                  {[
                    { id: 'all',     label: isAr ? 'الكل' : 'All' },
                    { id: 'factory', label: isAr ? 'عروض المصانع' : 'Factory' },
                    { id: 'managed', label: isAr ? 'مُدارة' : 'Managed' },
                    { id: 'idea',    label: isAr ? 'تصنيع فكرة' : 'Idea' },
                    { id: 'direct',  label: isAr ? 'مباشرة' : 'Direct' },
                  ].map(t => (
                    <button key={t.id} type="button" onClick={() => setReqTypeFilter(t.id)}
                      style={{
                        fontSize: 12, padding: '5px 12px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
                        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        border: '1px solid ' + (reqTypeFilter === t.id ? 'var(--text-primary)' : 'var(--border-subtle)'),
                        background: reqTypeFilter === t.id ? 'var(--text-primary)' : 'transparent',
                        color: reqTypeFilter === t.id ? 'var(--bg-raised)' : 'var(--text-secondary)',
                      }}>{t.label}</button>
                  ))}
                </div>
              )}

              {/* Loading skeleton */}
              {loadingRequests && [1, 2].map(i => (
                <div key={i} style={{ borderTop: '1px solid var(--border-subtle)', padding: '28px 0' }}>
                  <div style={{ width: '45%', height: 16, background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', marginBottom: 10 }} />
                  <div style={{ width: '25%', height: 10, background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)' }} />
                </div>
              ))}

              {/* Empty */}
              {!loadingRequests && myRequests.length === 0 && (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ fontSize: 14, color: 'var(--text-disabled)', marginBottom: 24, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'ما عندك طلبات بعد' : 'No requests yet'}
                  </p>
                  <button className="btn-dark-sm" onClick={() => nav('/requests')} style={{ minHeight: 40 }}>
                    {isAr ? 'ارفع أول طلب' : 'Post First Request'}
                  </button>
                </div>
              )}

              {/* Requests list */}
              {!loadingRequests && (() => {
                const filteredRequests = myRequests.filter(r => {
                  if (reqTypeFilter !== 'all' && requestType(r) !== reqTypeFilter) return false;
                  if (reqSubFilter === 'open')      return ['open','offers_received'].includes(r.status);
                  // Same exclusion as activeCount above, and it has to stay the
                  // same: a tab whose count disagrees with its contents reads
                  // as a data fault rather than a filter bug.
                  if (reqSubFilter === 'active')    return !['open','offers_received','delivered', CANCELLED].includes(r.status);
                  if (reqSubFilter === 'completed') return r.status === 'delivered';
                  return true;
                });
                if (filteredRequests.length === 0 && myRequests.length > 0) return (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-disabled)', fontSize: 13, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'لا توجد طلبات في هذا التصنيف' : 'No requests in this filter'}
                  </div>
                );
                return filteredRequests.map((r, idx) => {
                const managed = isManagedRequest(r);
                const pendingOffers = r.offers.filter(o => o.status === 'pending');
                const acceptedOffer = r.offers.find(o => o.status === 'accepted');
                const offerTotals = r.offers.map(o => getOfferEstimatedTotal(o, r)).filter(Number.isFinite);
                const lowestOfferTotal = offerTotals.length > 0 ? Math.min(...offerTotals) : null;
                const fastestOffer = r.offers.reduce((best, current) => {
                  if (!best) return current;
                  if ((parseInt(current.delivery_days, 10) || Infinity) < (parseInt(best.delivery_days, 10) || Infinity)) return current;
                  return best;
                }, null);
                const isFocusedRequest = String(focusedRequestId || '') === String(r.id);
                const isExpanded = expandedReq === r.id || isFocusedRequest;
                const feeAwaiting = r.fee?.status === 'awaiting_payment' && String(r.status || '').toLowerCase() === 'open';
                const nextStepCopy = (() => {
                  if (managed) {
                    if (String(r.managed_status || '') === 'offer_ready') {
                      return {
                        title: isAr ? 'الخطوة التالية: عرضك المنسّق جاهز' : 'Next step: your curated offer is ready',
                        body: isAr ? 'افتح صفحة المتابعة للاطلاع على العرض واعتماده ودفع العربون.' : 'Open the tracker to review the offer, approve it, and pay the deposit.',
                      };
                    }
                    return {
                      title: isAr ? 'الطلب الآن داخل المسار المُدار' : 'This request is now inside the managed flow',
                      body: isAr ? 'فريق مَعبر يدير طلبك خطوة بخطوة ويجهّز لك عرضاً منسّقاً. تابع كل التحديثات من صفحة المتابعة.' : 'The Maabar team manages your order step by step and prepares a curated offer. Track every update from the tracker page.',
                    };
                  }
                  if (acceptedOffer && r.status === 'closed') {
                    return {
                      title: isAr ? 'الخطوة التالية: انتظر تأكيد المورد' : 'Next step: waiting for supplier to confirm',
                      body: isAr ? 'تم قبول العرض — المورد سيراسلك لتأكيد التفاصيل ثم يفتح لك خيار الدفع.' : 'Offer accepted — the supplier will message you to confirm details, then unlock payment.',
                    };
                  }
                  if (acceptedOffer && r.status === 'supplier_confirmed') {
                    return {
                      title: isAr ? 'الخطوة التالية: المورد جاهز — ادفع بأمان داخل مَعبر' : 'Next step: supplier is ready — pay securely on Maabar',
                      body: isAr ? 'تم تأكيد التفاصيل. أكمل الدفع من نفس الطلب حتى يبقى السجل التجاري واضحاً للطرفين.' : 'Details confirmed. Complete checkout from this request so payment and execution stay on one clear record.',
                    };
                  }
                  if (r.status === 'ready_to_ship' && r.payment_second > 0) {
                    return {
                      title: isAr ? 'الخطوة التالية: ادفع الدفعة الثانية قبل إصدار الشحنة' : 'Next step: pay the second installment before shipment release',
                      body: isAr ? 'المورد أكد جاهزية الشحنة. أكمل الدفعة المتبقية من هذا الطلب حتى يبدأ الشحن.' : 'The supplier confirmed shipment readiness. Complete the remaining balance here to release shipping.',
                    };
                  }
                  if (r.status === 'shipping') {
                    return {
                      title: isAr ? 'الخطوة التالية: تابع التتبع ثم أكد وصول الشحنة للسعودية' : 'Next step: follow tracking, then confirm arrival in KSA',
                      body: isAr ? 'بمجرد وصول الشحنة يمكنك تحديث الحالة ثم تأكيد الاستلام لاحقاً.' : 'Once the shipment arrives, update the status here and confirm final delivery afterwards.',
                    };
                  }
                  if (r.status === 'arrived') {
                    return {
                      title: isAr ? 'الخطوة التالية: أكد الاستلام لإغلاق الصفقة' : 'Next step: confirm delivery to close the deal',
                      body: isAr ? 'إذا استلمت البضاعة كما هو متفق عليه، أكد الاستلام من هذا الطلب.' : 'If the goods arrived as agreed, confirm delivery from this request.',
                    };
                  }
                  if (pendingOffers.length > 0) {
                    return {
                      title: isAr ? `الخطوة التالية: قارن ${pendingOffers.length} عرض${pendingOffers.length > 1 ? 'اً' : ''} واختر الأنسب` : `Next step: compare ${pendingOffers.length} offer${pendingOffers.length > 1 ? 's' : ''} and pick the best fit`,
                      body: isAr ? 'راجع الإجمالي، مدة التجهيز، وطريقة الشحن قبل قبول العرض.' : 'Review total cost, lead time, and shipping method before accepting an offer.',
                    };
                  }
                  if (r.offers.length > 0) {
                    return {
                      title: isAr ? 'العروض موجودة داخل هذا الطلب' : 'Offers are already attached to this request',
                      body: isAr ? 'كل قرار لاحق — قبول، دفع، متابعة، أو استلام — يتم من نفس البطاقة.' : 'Every next decision — accept, pay, track, or confirm receipt — stays inside this same card.',
                    };
                  }
                  return null;
                })();

                return (
                <div key={r.id} data-request-id={r.id} style={{
                  borderTop: '1px solid var(--border-subtle)',
                  padding: '24px 0',
                  animation: `fadeIn 0.35s ease ${idx * 0.04}s both`,
                  scrollMarginTop: 110,
                  background: isFocusedRequest ? 'var(--bg-subtle)' : 'transparent',
                  boxShadow: 'none',
                  borderRadius: isFocusedRequest ? 'var(--radius-lg)' : 0,
                }}>
                  {/* ── Card header: category tag + title + time ── */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {(r.category_ar || r.category_en || r.category) && (
                          <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', border: '1px solid var(--border-subtle)', padding: '2px 8px', borderRadius: 'var(--radius-chip)' }}>
                            {isAr ? (r.category_ar || r.category) : (r.category_en || r.category)}
                          </span>
                        )}
                        {(() => {
                          const ty = requestType(r);
                          const label = ty === 'factory' ? (isAr ? 'عرض سعر مصنع' : 'Factory quote')
                            : ty === 'idea' ? (isAr ? 'تصنيع فكرة' : 'Idea to product')
                              : ty === 'managed' ? (isAr ? 'طلب مُدار' : 'Managed') : null;
                          return label ? (
                            <span style={{ fontSize: 9, letterSpacing: isAr ? 0 : 1.5, textTransform: isAr ? 'none' : 'uppercase', color: 'var(--text-disabled)', border: '1px solid var(--border-subtle)', padding: '2px 8px', borderRadius: 'var(--radius-chip)' }}>
                              {label}
                            </span>
                          ) : null;
                        })()}
                        <span style={{ fontSize: 10, color: 'var(--text-disabled)', marginInlineStart: 'auto' }}>
                          {relativeTime(r.created_at, isAr)}
                        </span>
                        <button type="button" onClick={() => setExpandedReq(isExpanded ? null : r.id)}
                          aria-label={isExpanded ? (isAr ? 'طيّ' : 'Collapse') : (isAr ? 'توسيع' : 'Expand')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1, padding: '2px 4px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</button>
                      </div>
                      <h3 onClick={() => setExpandedReq(isExpanded ? null : r.id)} style={{ fontSize: 16, fontWeight: 500, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)', lineHeight: 1.3, cursor: 'pointer' }}>
                        {isAr ? r.title_ar || r.title_en : r.title_en || r.title_ar}
                      </h3>
                      <p style={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'الكمية: ' : 'Qty: '}{r.quantity || '—'}
                        {/* CANCELLED renders for EVERY kind, managed included.
                            The status line below is gated on !managed, so a
                            cancelled managed request showed no status at all --
                            a row that simply lost its buttons and looked empty.
                            This sits outside that gate on purpose. */}
                        {r.status === CANCELLED && (
                          <span style={{
                            marginInlineStart: 10, padding: '2px 9px', borderRadius: 'var(--radius-pill)',
                            border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', fontSize: 10,
                            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                          }}>
                            {isAr ? 'ملغي' : lang === 'zh' ? '已取消' : 'Cancelled'}
                          </span>
                        )}
                        {r.status !== CANCELLED && !managed && (() => {
                          // ONE SOURCE. requests.status says 'offers_received'
                          // the moment ANY offer inserts, including one gated
                          // because the supplier is unverified — which is how
                          // M-10074 showed "عروض وصلت" above a body reading
                          // "لا توجد عروض بعد" with every counter at zero.
                          // The label is derived from what the trader can
                          // actually see, and the gated ones get their own
                          // truthful line rather than being folded in.
                          const visibleOffers = (r.offers || []).length;
                          const held = pendingReview[r.id] || 0;
                          const shownStatus = ['open', 'offers_received'].includes(r.status)
                            ? (visibleOffers > 0 ? 'offers_received' : 'open')
                            : r.status;
                          return (
                            <>
                              <span style={{ marginInlineStart: 10 }}>
                                {/* Was isAr ? AR : EN, so a zh trader read English. */}
                                {requestStatusLabel(shownStatus, lang)}
                              </span>
                              {held > 0 && (
                                <span style={{ marginInlineStart: 10, color: 'var(--text-secondary)' }}>
                                  {isAr
                                    ? `· ${held} ${held === 1 ? 'عرض' : 'عروض'} قيد المراجعة لدى مَعبر`
                                    : lang === 'zh'
                                      ? `· ${held} 份报价正在 Maabar 审核中`
                                      : `· ${held} offer${held === 1 ? '' : 's'} under review by Maabar`}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                    {/* ── What the trader may do with this request ────────────
                        EDIT is keyed on ONE fact and is deliberately NOT part
                        of the three-stage gate: has anyone quoted yet? While no
                        offer exists the brief is still being shaped, and that
                        is as true of a managed request as of a direct one. The
                        moment any offer exists Edit goes -- suppliers priced
                        against what they read, and moving the brief underneath
                        a quote invalidates it silently.

                        DELETE and CANCEL follow the three stages
                        (migration 20260920120000):
                          1  open, zero offers, not managed   Delete
                          2  offers exist, or managed          Cancel
                          3  accepted, or paid                 neither
                        The RLS is the enforcement; this decides what is even
                        offered, so the trader is never shown a button the
                        database will refuse.

                        ⚠️ EDIT USED TO RIDE ON THE STAGE-1 BLOCK, sharing its
                        condition with Delete. Making managed requests stage 2
                        from creation therefore took Edit away from them as a
                        side effect -- a regression nobody intended, because the
                        rule for Edit had never been written down anywhere. It
                        is its own condition now so the next change to the
                        stages cannot move it again by accident. */}
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {canEditRequest(r) && (
                        <button onClick={() => { setEditReqModal(r); setEditReqError(''); setEditReqForm({ title_ar: r.title_ar || '', title_en: r.title_en || '', desc_ar: r.description_ar || r.description || '', quantity: r.quantity || '' }); }}
                          className="btn-outline" style={{ padding: '3px 8px', fontSize: 10, minHeight: 24 }}>
                          {isAr ? 'تعديل' : 'Edit'}
                        </button>
                      )}

                      {r.status === 'open' && requestStage(r) === 1 && (
                        <button onClick={() => deleteRequest(r)}
                          style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 24 }}>
                          {isAr ? 'حذف' : 'Delete'}
                        </button>
                      )}

                      {requestStage(r) === 2 && !['closed', 'cancelled'].includes(r.status) && (
                        <button onClick={() => setCancelConfirmReq(r)}
                          style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 24, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'إلغاء الطلب' : 'Cancel request'}
                        </button>
                      )}
                    </div>
                    {['paid','ready_to_ship','shipping','arrived','delivered'].includes(r.status) && (
                      <a href={`mailto:support@maabar.io?subject=${encodeURIComponent((isAr ? 'مشكلة في طلب: ' : 'Issue with order: ') + (r.title_ar || r.title_en || r.id))}&body=${encodeURIComponent((isAr ? 'رقم الطلب: ' : 'Order ID: ') + r.id)}`}
                        style={{ fontSize: 10, color: 'var(--red)', textDecoration: 'none', border: '1px solid rgba(224,92,92,0.25)', padding: '3px 8px', borderRadius: 'var(--radius-md)', flexShrink: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'إبلاغ' : 'Report'}
                      </a>
                    )}
                  </div>

                  {/* ── Variant line items (Phase 4) ── */}
                  {(r.lineItems || []).length > 0 && (
                    <div style={{ margin: '10px 0', padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
                      <p style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8 }}>
                        {isAr ? 'المنتجات المطلوبة' : 'Order Lines'}
                      </p>
                      {(r.lineItems || []).map((li, idx) => (
                        <div key={li.id || idx} style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-secondary)', padding: '4px 0', borderBottom: idx < r.lineItems.length - 1 ? '1px solid var(--border-subtle)' : 'none', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace', flex: 1 }}>{li.product_variants?.sku || li.variant_id?.slice(0, 8) || '—'}</span>
                          <span>×{li.quantity}</span>
                          {/* SAR only (owner, 2026-09-29) — was a hardcoded "$". */}
                          <span style={{ direction: 'ltr' }}>{toSarText(li.unit_price_usd, 'USD')}/u</span>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)', direction: 'ltr' }}>{toSarText(li.quantity * Number(li.unit_price_usd || 0), 'USD')}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Supplier strip (when accepted offer) ── */}
                  {!managed && acceptedOffer && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 2px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                        {acceptedOffer.profiles?.company_name || '—'}
                      </span>
                      {getSupplierMaabarId(acceptedOffer.profiles || {}) && (
                        <span style={{ fontSize: 10, color: 'var(--text-disabled)' }}>
                          · {getSupplierMaabarId(acceptedOffer.profiles)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── The request fee, when unpaid: shown folded or not — until it is
                      paid the Maabar team has not started, and this is the only
                      thing the trader can do about it. ── */}
                  {managed && feeAwaiting && (
                    <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)' }}>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {feeNoticeText(isAr ? 'ar' : 'en', r.fee.amount)}
                      </p>
                      <button type="button" disabled={Boolean(directOrderPaying[r.id])}
                        onClick={async () => {
                          setDirectOrderPaying((p) => ({ ...p, [r.id]: true }));
                          try { await payRequestFee(r.id); }
                          catch (e) {
                            console.error('[DashboardBuyer] could not start the fee payment:', e?.status || '', e?.message || e);
                            alert(paymentStartErrorAlert(e, lang));
                            setDirectOrderPaying((p) => ({ ...p, [r.id]: false }));
                          }
                        }}
                        style={{ width: '100%', margin: 0, padding: '11px', borderRadius: 'var(--radius-lg)', border: 'none', background: '#1a1814', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? `ادفع رسوم تأكيد الطلب — ${formatSar(r.fee.amount)} ر.س` : `Pay the request confirmation fee — SAR ${formatSar(r.fee.amount)}`}
                      </button>
                    </div>
                  )}
                  {managed && r.fee && !feeAwaiting && feeStatusText(r.fee.status, isAr ? 'ar' : 'en') && (
                    <p style={{ fontSize: 11.5, color: 'var(--text-disabled)', margin: '0 0 8px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {feeStatusText(r.fee.status, isAr ? 'ar' : 'en')}
                    </p>
                  )}
                  {/* ── Folded detail: everything below shows only when expanded ── */}
                  {isExpanded && (<>
                  {/* ── Status timeline (direct/factory orders) ── */}
                  {/* The row itself, not a pre-picked status: the bar reads
                      shipping_status for the STAGE and status for CANCELLATION,
                      and a call site that has to remember both will forget one. */}
                  {!managed && <RequestTimeline request={r} lang={lang} />}

                  {/* ── Managed order: the dedicated tracker is the single source of
                      truth — updates, the curated offer, and payment all live there. ── */}
                  {managed && !feeAwaiting && (
                    <div style={{ marginBottom: 14 }}>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {String(r.managed_status || '') === 'offer_ready'
                          ? (isAr ? 'عرضك المنسّق جاهز — افتح صفحة المتابعة للاطلاع عليه واعتماده.' : 'Your curated offer is ready — open the tracker to review and approve it.')
                          : (isAr ? 'فريق مَعبر يدير طلبك خطوة بخطوة. تابع كل التفاصيل والعرض والدفع من صفحة المتابعة.' : 'The Maabar team manages your order step by step. Track every update, the offer, and payment from the tracker.')}
                      </p>
                      <button type="button" onClick={() => nav('/managed-order/' + r.id)}
                        style={{ width: '100%', margin: 0, padding: '11px', borderRadius: 'var(--radius-lg)', border: 'none', background: '#1a1814', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'متابعة الطلب المُدار ←' : 'Track managed order →'}
                      </button>
                    </div>
                  )}

                  {/* ── Payment plan row ── */}
                  {!managed && acceptedOffer && (
                    <PaymentPlanRow request={r} offer={acceptedOffer} isAr={isAr} lang={lang} />
                  )}

                  {/* ── Tracking card ── */}
                  {!managed && <TrackingCard request={r} isAr={isAr} />}

                  {/* ── Compare offers grid (pending offers only, or all when comparing) ── */}
                  {!managed && pendingOffers.length > 0 && (
                    <div>
                      {pendingOffers.length > 1 && (
                        <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10 }}>
                          {isAr ? `${pendingOffers.length} عروض — قارن واختر` : `${pendingOffers.length} Offers — Compare & Choose`}
                        </p>
                      )}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.min(pendingOffers.length, 3)}, 1fr)`,
                        gap: 1,
                        background: 'var(--border-subtle)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                      }}>
                        {pendingOffers.map(o => {
                          // Compared in SAR: raw totals in different currencies
                          // (a USD offer beside a SAR one) are not comparable.
                          const sarTotal = (x) => (x.sar?.payable ? Number(x.sar.goods_sar) : null);
                          const offerEstimatedTotal = sarTotal(o);
                          const comparable = pendingOffers.map(sarTotal).filter((v) => v != null);
                          const lowestOfferTotal = comparable.length ? Math.min(...comparable) : null;

                          return (
                          <div key={o.id} style={{
                            background: o.status === 'accepted' ? 'var(--bg-raised)' : 'var(--bg-subtle)',
                            padding: '18px 16px',
                            position: 'relative',
                          }}>
                            {r.offers.length > 1 && offerEstimatedTotal != null && offerEstimatedTotal === lowestOfferTotal && (
                              <span style={{
                                position: 'absolute', top: 10,
                                insetInlineEnd: 10,
                                fontSize: 9, padding: '2px 8px',
                                background: 'rgba(45,122,79,0.12)',
                                border: '1px solid rgba(45,122,79,0.2)',
                                color: 'var(--green)', borderRadius: 'var(--radius-pill)',
                              }}>
                                {isAr ? 'الأقل إجمالاً' : lang === 'zh' ? '总价最低' : 'Lowest Total'}
                              </span>
                            )}
                            {(() => {
                              const supplierTrustSignals = buildSupplierTrustSignals(o.profiles || {});
                              const supplierMaabarId = getSupplierMaabarId(o.profiles || {});

                              return (
                                <>
                                  <div style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                                      <p style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 0, fontWeight: 500 }}>
                                        {o.profiles?.company_name || '—'}
                                      </p>
                                    </div>
                                    {o.profiles?.rating > 0 && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
                                        <span style={{ color: 'var(--warn)', fontSize: 11 }}>{'★'.repeat(Math.round(o.profiles.rating))}{'☆'.repeat(5 - Math.round(o.profiles.rating))}</span>
                                        <span style={{ fontSize: 10, color: 'var(--text-disabled)' }}>{o.profiles.rating.toFixed(1)} ({o.profiles.reviews_count || 0})</span>
                                      </div>
                                    )}
                                    {(supplierMaabarId || o.profiles?.city || o.profiles?.country || o.profiles?.years_experience) && (
                                      <p style={{ fontSize: 10, color: 'var(--text-disabled)', lineHeight: 1.6, marginBottom: 0 }}>
                                        {supplierMaabarId ? `${isAr ? 'معرّف المورد' : lang === 'zh' ? '供应商编号' : 'Supplier ID'}: ${supplierMaabarId}` : ''}
                                        {supplierMaabarId && (o.profiles?.city || o.profiles?.country) ? ' · ' : ''}
                                        {[o.profiles?.city, o.profiles?.country].filter(Boolean).join(', ')}
                                        {(supplierMaabarId || o.profiles?.city || o.profiles?.country) && o.profiles?.years_experience ? ' · ' : ''}
                                        {o.profiles?.years_experience ? (isAr ? `${o.profiles.years_experience} سنة خبرة` : lang === 'zh' ? `${o.profiles.years_experience} 年经验` : `${o.profiles.years_experience} years`) : ''}
                                      </p>
                                    )}
                                    {/* WHO DELIVERS TO ME. Directly under the
                                        company making the promise, not under
                                        the price — the trader is choosing a
                                        supplier, and how far that supplier
                                        carries the goods is part of who they
                                        are, not a footnote on the number. */}
                                    <DeliveryRow basis={o.price_basis} request={r} lang={lang} isAr={isAr} />
                                  </div>

                                  {supplierTrustSignals.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                                      {supplierTrustSignals.includes('factory_media_available') && (
                                        <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                                          {isAr ? 'صور مصنع' : lang === 'zh' ? '工厂图片' : 'Factory photos'}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {(() => {
                                    // SAR only — from o.sar (offer_amounts_sar). The
                                    // supplier's currency and the rate never reach
                                    // the trader. No amount → a dash, never a guess.
                                    const sa = o.sar?.payable ? o.sar : null;
                                    const sarTxt = (v) => (v == null ? '—' : `${formatSar(v)} ${isAr ? 'ر.س' : 'SAR'}`);
                                    // Owner, 2026-09-30: a quantity that is not a plain
                                    // number ("100 إلى 150 قطعة") is shown as written, with
                                    // NO products total and NO estimated total — the
                                    // server's figures would be built on a quantity of 1.
                                    const plainQty = isPlainQuantity(r.quantity);
                                    return (
                                  <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                                    <p style={{ fontSize: 24, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1 }}>
                                      {sa ? <>{formatSar(sa.unit_price_sar)} <span style={{ fontSize: 11, color: 'var(--text-disabled)' }}>{isAr ? 'ر.س' : 'SAR'}</span></> : '—'}
                                    </p>
                                    <p style={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                                      {isAr ? 'سعر الوحدة' : 'Unit price'}
                                    </p>
                                    {plainQty ? (
                                      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                        {isAr ? 'تكلفة المنتجات' : 'Products total'}: <Num v={sarTxt(sa?.products_sar)} />
                                      </p>
                                    ) : (
                                      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                        {isAr ? 'الكمية المطلوبة' : lang === 'zh' ? '需求数量' : 'Quantity requested'}: {String(r.quantity || '').trim() || '—'}
                                      </p>
                                    )}
                                    {/* ═══ CHANGED 2026-09-20 ═══════════════════════════════
                                        The fallback "غير محدد بشكل منفصل / Not specified
                                        separately" is GONE. offers.shipping_cost and
                                        .shipping_method were deprecated by 20260919105244
                                        ("no longer collected ... Do not add new writers");
                                        `price_basis` replaced them. The columns were NOT
                                        dropped, so a legacy offer that carries a cost still
                                        shows it — conditional, like the method line below. */}
                                    {hasOfferShippingCost(o) && (
                                      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                        {isAr ? 'الشحن' : 'Shipping'}: <Num v={sarTxt(sa?.shipping_sar)} />
                                      </p>
                                    )}
                                    {getOfferShippingMethod(o, lang) && (
                                      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                        {isAr ? 'طريقة الشحن' : lang === 'zh' ? '运输方式' : 'Shipping method'}: {getOfferShippingMethod(o, lang)}
                                      </p>
                                    )}
                                    {plainQty && (
                                      <p style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                                        {isAr ? 'الإجمالي التقديري' : 'Estimated total'}: <Num v={sarTxt(sa?.goods_sar)} />
                                      </p>
                                    )}
                                    {sa && (
                                      <p style={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                                        {sarFixedNote('offer', isAr ? 'ar' : lang)}
                                      </p>
                                    )}
                                    <p style={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                                      MOQ: {formatMoq(o.moq) || (isAr ? 'غير محدد' : lang === 'zh' ? '未注明' : 'Not specified')} · {o.delivery_days} {isAr ? 'يوم' : lang === 'zh' ? '天' : 'd'}{o.origin ? ` · ${isAr ? 'بلد التصنيع' : lang === 'zh' ? '生产国' : 'Made in'}: ${countryOfManufacture(o.origin, lang)}` : ''}
                                    </p>
                                    {/* ADDED 2026-09-21 — two facts that were
                                        COLLECTED and shown to nobody.

                                        VALIDITY: offers.valid_days has been
                                        stored since 20260918230823 and
                                        rendered on no surface at all, so a
                                        quote read as an open offer forever and
                                        a trader could accept a price the
                                        factory considered dead. Display only —
                                        nothing here blocks accepting an
                                        expired offer, because the database has
                                        no such rule and a client that refused
                                        what the server permits is a second
                                        authority on one decision.

                                        SAMPLE: the supplier is asked this ONLY
                                        because the trader marked a sample
                                        preferred or required. Their answer sat
                                        in the row unread — including when the
                                        trader made it mandatory. */}
                                    {(() => {
                                      const v = offerValidityText(o, lang);
                                      if (!v) return null;
                                      return (
                                        <p style={{ fontSize: 11, color: v.expired ? 'var(--red)' : 'var(--text-disabled)' }}>
                                          {v.text}
                                        </p>
                                      );
                                    })()}
                                    {(() => {
                                      const sample = offerSampleText(o, lang, () => sarTxt(sa?.sample_price_sar));
                                      if (!sample) return null;
                                      return (
                                        <p style={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                                          {isAr ? 'العيّنة: ' : lang === 'zh' ? '样品：' : 'Sample: '}{sample}
                                        </p>
                                      );
                                    })()}
                                  </div>
                                    );
                                  })()}

                                  {o.note && (
                                    <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
                                      <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 6 }}>
                                        {isAr ? 'ملاحظة تجارية' : lang === 'zh' ? '商务备注' : 'Commercial note'}
                                      </p>
                                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                                        <TranslatedText text={o.note} lang={lang} />
                                      </p>
                                    </div>
                                  )}
                                </>
                              );
                            })()}

                            {/* Pending offer action buttons.

                                ⚠️ ACCEPT AND REJECT ARE HIDDEN ON A CANCELLED
                                REQUEST, not disabled. A trader accepted an
                                offer on a request they had already cancelled
                                (2026-09-20) and it went through: the request
                                revived to 'closed', the ملغي badge vanished,
                                the payment plan appeared, and they were
                                committed to a deal in one click.

                                The DATABASE is what refuses it now — policy
                                offers_accept_only_when_offerable and trigger
                                trg_requests_no_revive, migration
                                20260920210000. This is only so the action is
                                never offered. Never the other way round: a
                                hidden button is manners, RLS is the guard.

                                `r.requestCancelled` comes from the data layer
                                (loadMyRequests), computed off the raw
                                requests.status via isRequestCancelled. It is
                                NOT recomputed here — see the note there.

                                The offer card itself stays visible: the quote
                                is a record of work a supplier did, and the
                                trader should still be able to read it. Same
                                treatment as the supplier side. */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                              {r.requestCancelled ? (
                                <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-disabled)', lineHeight: 1.5, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                                  {isAr ? 'هذا الطلب ملغي' : lang === 'zh' ? '此需求已取消' : 'This request is cancelled'}
                                </p>
                              ) : (
                              <>
                              <button onClick={() => acceptOffer(o.id, o.supplier_id, r.id)} className="btn-primary"
                                style={{ padding: '8px', fontSize: 11, letterSpacing: 1, width: '100%', minHeight: 34 }}>
                                {isAr ? 'قبول' : lang === 'zh' ? '接受报价' : 'Accept'}
                              </button>
                              <button onClick={() => rejectOffer(o.id, o.supplier_id, r.id)}
                                style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '7px', fontSize: 11, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 34, width: '100%' }}>
                                {isAr ? 'رفض' : lang === 'zh' ? '拒绝' : 'Reject'}
                              </button>
                              </>
                              )}
                              <button onClick={() => nav(`/supplier/${o.supplier_id}`)} className="btn-outline"
                                style={{ padding: '7px', fontSize: 11, letterSpacing: 1, width: '100%', minHeight: 34 }}>
                                {isAr ? 'ملف المورد' : lang === 'zh' ? '供应商主页' : 'Supplier Profile'}
                              </button>
                              <button onClick={() => nav(`/chat/${o.supplier_id}`)} className="btn-outline"
                                style={{ padding: '7px', fontSize: 11, letterSpacing: 1, width: '100%', minHeight: 34 }}>
                                {isAr ? 'تواصل' : lang === 'zh' ? '联系' : 'Chat'}
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Status-driven action area (accepted offer) ── */}
                  {!managed && acceptedOffer && (() => {
                    const o = acceptedOffer;
                    const subtotal  = (o.price || 0) * (Number(r.quantity) || 1);
                    const shipping  = parseFloat(o.shipping_cost) || 0;
                    const total     = subtotal + shipping;
                    // Only decides whether a balance is still owed; the amount
                    // itself is charged (and shown) by Telr from the server side.
                    // 0 for a full payment (lib/paymentProgress, item 7ج) —
                    // so no "pay the remaining balance" after paying in full.
                    const secondAmt = balanceDue(r, total);
                    // What Telr will charge, to the halala: payment_quote(),
                    // loaded with the request — the same call telr-create
                    // charges from. Goods at the rate locked when the offer
                    // was accepted, plus the 5% fee on its own line.
                    const q = r.quote;
                    const payable = Boolean(q?.payable);
                    const depositSar = stageAmountSar(q, 'deposit');
                    const totalSar = stageAmountSar(q, 'full');
                    const balanceSar = stageAmountSar(q, 'balance');
                    // The request fee credit — its own line, SAR only; the Pay
                    // buttons above already carry it (payment_quote *_due_sar).
                    const creditSar = feeCreditSar(q);
                    const sar = isAr ? 'ر.س' : 'SAR';
                    const payLine = { fontSize: 12, color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', gap: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
                    const num = { direction: 'ltr', fontVariantNumeric: 'tabular-nums' };
                    const chargeSummary = payable ? (
                      <div style={{ padding: '10px 12px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={payLine}>
                          <span>{isAr ? 'قيمة البضاعة' : lang === 'zh' ? '货款' : 'Goods'}</span>
                          <span style={num}>
                            {`${formatSar(q.goods_sar)} ${sar}`}
                          </span>
                        </div>
                        {/* no line for a «حصريات معبر» invoice: it carries no fee */}
                        {Number(q.fee_sar) > 0 && (
                        <div style={payLine}>
                          <span>{isAr ? 'رسوم معبر 5%' : lang === 'zh' ? 'Maabar 服务费 5%' : 'Maabar fee 5%'}</span>
                          <span style={num}>{formatSar(q.fee_sar)} {sar}</span>
                        </div>
                        )}
                        <div style={{ ...payLine, color: 'var(--text-primary)', fontWeight: 600 }}>
                          <span>{isAr ? 'الإجمالي' : lang === 'zh' ? '合计' : 'Total'}</span>
                          <span style={num}>{formatSar(q.total_sar)} {sar}</span>
                        </div>
                        {creditSar > 0 && (
                          <div style={payLine}>
                            <span>{feeCreditLabel(isAr ? 'ar' : 'en')}</span>
                            <span style={num}>−{formatSar(creditSar)} {sar}</span>
                          </div>
                        )}
                        <p style={{ margin: 0, fontSize: 11, color: 'var(--text-disabled)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {sarFixedNote('offer', isAr ? 'ar' : lang)}
                        </p>
                      </div>
                    ) : q?.reason === 'currency_not_payable' || q?.reason === 'rate_not_locked' ? (
                      // telr-create refuses this currency (409) — say so rather
                      // than offer a Pay button that cannot work.
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'الدفع الإلكتروني غير متاح لهذا العرض بعد. تواصل مع المورد أو مع دعم معبر.'
                          : "This offer can't be paid online yet. Contact the supplier or Maabar support."}
                      </p>
                    ) : (
                      // No quote (the call failed) or an amount the server
                      // refuses: no Pay button, and a line that says why.
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'تعذّر حساب المبلغ المستحق الآن. حدّث الصفحة، وإن استمر تواصل مع دعم معبر.'
                          : lang === 'zh' ? '暂时无法计算应付金额。请刷新页面；如仍有问题，请联系 Maabar 客服。'
                          : "We couldn't work out the amount due right now. Refresh the page, and contact Maabar support if it persists."}
                      </p>
                    );

                    const btnPrimary = { padding: '11px 18px', fontSize: 13, minHeight: 44, width: '100%', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : 0.5 };
                    const btnOutline = { ...btnPrimary, background: 'none', border: '1px solid var(--border-muted)', color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 'var(--radius-md)' };
                    // btnDanger removed with the stage-3 cancel buttons it
                    // styled: there is no destructive action on this card any
                    // more, which is the point of the three-stage rule.

                    return (
                      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0', borderTop: '1px solid var(--border-subtle)' }}>

                        {r.status === 'closed' && (
                          <>
                            <p style={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', marginBottom: 4 }}>
                              {isAr ? 'في انتظار تأكيد المورد' : 'Waiting for supplier confirmation'}
                            </p>
                            <button onClick={() => nav(`/chat/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'تواصل مع المورد' : 'Chat with Supplier'}
                            </button>
                          </>
                        )}

                        {r.status === 'supplier_confirmed' && (
                          <>
                            {/* The amounts on these buttons ARE the amounts Telr
                                charges (both read payment_quote). */}
                            {chargeSummary}
                            {payable && totalSar !== null && (
                              <>
                                {/* No deposit button when the request fee credit
                                    covers the whole deposit — full payment only. */}
                                {depositSar !== null && (
                                <button disabled={Boolean(directOrderPaying[r.id])} onClick={() => payOfferStage(r.id, 'deposit')} className="btn-primary" style={btnPrimary}>
                                  {isAr ? `ادفع عربون 30% — ${formatSar(depositSar)} ${sar}` : lang === 'zh' ? `支付 30% 定金 — ${formatSar(depositSar)} SAR` : `Pay 30% deposit — ${formatSar(depositSar)} SAR`}
                                </button>
                                )}
                                <button disabled={Boolean(directOrderPaying[r.id])} onClick={() => payOfferStage(r.id, 'full')} style={depositSar !== null ? btnOutline : btnPrimary}>
                                  {isAr ? `ادفع المبلغ كاملاً — ${formatSar(totalSar)} ${sar}` : lang === 'zh' ? `支付全款 — ${formatSar(totalSar)} SAR` : `Pay in full — ${formatSar(totalSar)} SAR`}
                                </button>
                              </>
                            )}
                            <button onClick={() => nav(`/chat/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'تواصل مع المورد' : 'Chat with Supplier'}
                            </button>
                          </>
                        )}

                        {r.status === 'paid' && (
                          <>
                            <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', padding: '4px 0' }}>
                              {isAr ? 'في انتظار الإنتاج — المورد يجهّز شحنتك' : 'Awaiting production — supplier is preparing your order'}
                            </p>
                            <button onClick={() => nav(`/chat/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'تواصل مع المورد' : 'Chat with Supplier'}
                            </button>
                          </>
                        )}

                        {r.status === 'ready_to_ship' && secondAmt > 0 && (
                          <>
                            {chargeSummary}
                            {payable && (
                              <button disabled={Boolean(directOrderPaying[r.id])} onClick={() => payOfferStage(r.id, 'balance')} className="btn-primary" style={btnPrimary}>
                                {(isAr ? 'ادفع المبلغ المتبقي' : lang === 'zh' ? '支付尾款' : 'Pay the remaining balance')
                                  + (balanceSar !== null ? ` — ${formatSar(balanceSar)} ${sar}` : '')}
                              </button>
                            )}
                            <button onClick={() => nav(`/chat/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'تواصل مع المورد' : 'Chat with Supplier'}
                            </button>
                          </>
                        )}

                        {r.status === 'shipping' && (
                          <>
                            {secondAmt > 0 && payable && (
                              <button disabled={Boolean(directOrderPaying[r.id])} onClick={() => payOfferStage(r.id, 'balance')} className="btn-primary" style={btnPrimary}>
                                {(isAr ? 'ادفع المبلغ المتبقي' : lang === 'zh' ? '支付尾款' : 'Pay the remaining balance')
                                  + (balanceSar !== null ? ` — ${formatSar(balanceSar)} ${sar}` : '')}
                              </button>
                            )}
                            <button onClick={async () => { await sb.from('requests').update({ status: 'arrived', shipping_status: 'arrived' }).eq('id', r.id); loadMyRequests(); }} style={btnOutline}>
                              {isAr ? 'استلمت الشحنة' : 'Shipment Received'}
                            </button>
                            <button onClick={() => nav(`/chat/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'تواصل مع المورد' : 'Chat with Supplier'}
                            </button>
                          </>
                        )}

                        {r.status === 'arrived' && (
                          <button onClick={() => confirmDelivery(r.id, o.supplier_id, o.profiles?.company_name)} className="btn-primary"
                            style={{ ...btnPrimary, background: 'rgba(45,122,79,0.9)', borderColor: 'transparent' }}>
                            {isAr ? 'تأكيد الاستلام' : 'Confirm Delivery'}
                          </button>
                        )}

                        {r.status === 'delivered' && (
                          <>
                            <p style={{ fontSize: 12, color: 'var(--green)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {isAr ? 'تم التسليم ✓' : 'Delivered ✓'}
                            </p>
                            {!submittedReviewIds.has(r.id) && (
                              <button onClick={() => setReviewModal({ supplierId: o.supplier_id, requestId: r.id, supplierName: o.profiles?.company_name || '' })}
                                style={btnOutline}>
                                {isAr ? 'قيّم المورد' : 'Rate Supplier'}
                              </button>
                            )}
                            <button onClick={() => nav(`/supplier/${o.supplier_id}`)} style={btnOutline}>
                              {isAr ? 'ملف المورد' : 'Supplier Profile'}
                            </button>
                          </>
                        )}

                      </div>
                    );
                  })()}

                  {!managed && r.offers.length === 0 && (
                    <p style={{ color: 'var(--text-disabled)', fontSize: 12, marginTop: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {isAr ? 'لا توجد عروض بعد' : 'No offers yet'}
                    </p>
                  )}
                  </>)}
                </div>
              );
              });
              })()}
            </div>
          )}

          {/* ── DIRECT PURCHASE ORDERS (Step 4) ── */}
          {activeTab === 'direct-orders' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} isAr={isAr} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'مشترياتي المباشرة' : lang === 'zh' ? '我的直接采购' : 'My Direct Purchases'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 28, lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr
                  ? 'الطلبات التي اشتريتها مباشرة من صفحات المنتجات. عند تأكيد المورد، تظهر هنا «ادفع الآن».'
                  : lang === 'zh'
                    ? '您从产品页面直接下单的采购订单。供应商确认后，此处会出现「立即付款」。'
                    : 'Orders you placed directly from product pages. When the supplier confirms, "Pay Now" appears here.'}
              </p>

              {loadingDirectOrders && (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 13, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'جاري التحميل…' : lang === 'zh' ? '加载中…' : 'Loading…'}
                  </p>
                </div>
              )}

              {!loadingDirectOrders && directOrders.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, marginBottom: 16, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'لا توجد مشتريات مباشرة بعد' : lang === 'zh' ? '暂无直接采购订单' : 'No direct purchases yet'}
                  </p>
                  <button onClick={() => nav(hioBuyOnly ? '/market' : '/products')} className="btn-outline" style={{ padding: '8px 20px', fontSize: 12, minHeight: 36 }}>
                    {isAr ? 'تصفح المنتجات' : lang === 'zh' ? '浏览产品' : 'Browse Products'}
                  </button>
                </div>
              )}

              {!loadingDirectOrders && directOrders.map(r => {
                const product = r.product || {};
                const supplierProfile = product.profiles || null;
                const productName = lang === 'zh'
                  ? (product.name_zh || product.name_en || product.name_ar)
                  : lang === 'en'
                    ? (product.name_en || product.name_ar || product.name_zh)
                    : (product.name_ar || product.name_en || product.name_zh);
                const supplierName = supplierProfile?.company_name || supplierProfile?.full_name || (isAr ? 'مورد' : lang === 'zh' ? '供应商' : 'Supplier');
                const unitPrice = Number(deriveProductPriceFrom(product) || 0);
                const currency = product.currency || 'USD';
                const qty = Number(r.quantity || 0);
                const totalEstimate = unitPrice * qty;
                const productImage = getPrimaryProductImage(product);
                const paying = Boolean(directOrderPaying[r.id]);

                // Status-specific copy + UI tone
                const statusInfo = (() => {
                  if (r.status === 'pending_supplier_confirmation') return {
                    label: isAr ? 'بانتظار تأكيد المورد' : lang === 'zh' ? '等待供应商确认' : 'Awaiting Supplier',
                    color: 'var(--warn)',
                    bg: 'rgba(160,136,80,0.05)',
                    border: 'rgba(160,136,80,0.30)',
                    body: isAr ? 'سيؤكد المورد طلبك خلال 24 ساعة. ستصلك إشعار فور الرد.' : lang === 'zh' ? '供应商将在 24 小时内确认订单，回复后您会收到通知。' : 'The supplier will confirm within 24 hours. You will be notified once they respond.',
                  };
                  if (r.status === 'supplier_confirmed') return {
                    label: isAr ? 'تم التأكيد — ادفع الآن' : lang === 'zh' ? '已确认 — 请立即付款' : 'Confirmed — Pay Now',
                    color: 'var(--green)',
                    bg: 'rgba(45,122,79,0.06)',
                    border: 'rgba(45,122,79,0.35)',
                    body: isAr ? 'أكد المورد جاهزيته لتنفيذ طلبك. أكمل الدفع الكامل ليبدأ بالتجهيز.' : lang === 'zh' ? '供应商已确认接单。请完成全额付款，供应商将立即开始备货。' : 'Supplier confirmed. Pay the full amount to start preparation.',
                  };
                  if (r.status === 'supplier_rejected') return {
                    label: isAr ? 'رفض المورد' : lang === 'zh' ? '供应商已拒绝' : 'Supplier Declined',
                    color: 'var(--red)',
                    bg: 'rgba(224,92,92,0.05)',
                    border: 'rgba(224,92,92,0.30)',
                    body: isAr ? 'لم يتمكن المورد من تنفيذ طلبك. يمكنك تصفح موردين آخرين.' : lang === 'zh' ? '供应商无法接受此订单。您可以浏览其他供应商。' : 'The supplier could not fulfill this order. You can browse other suppliers.',
                  };
                  if (r.status === 'paid') return {
                    label: isAr ? 'تم الدفع — في انتظار التجهيز' : lang === 'zh' ? '已付款 — 等待备货' : 'Paid — Awaiting Preparation',
                    color: 'var(--green)',
                    bg: 'rgba(45,122,79,0.05)',
                    border: 'rgba(45,122,79,0.25)',
                    body: isAr ? 'تم استلام دفعتك. المورد سيبدأ التجهيز ويرسل رقم التتبع قريباً.' : lang === 'zh' ? '已收到您的付款。供应商将开始备货，并尽快提供物流单号。' : 'Payment received. The supplier will prepare your order and share tracking shortly.',
                  };
                  if (r.status === 'ready_to_ship') return {
                    label: isAr ? 'الشحنة جاهزة' : lang === 'zh' ? '准备发货' : 'Ready to Ship',
                    color: 'var(--warn)',
                    bg: 'rgba(160,136,80,0.05)',
                    border: 'rgba(160,136,80,0.30)',
                    body: isAr ? 'المورد جهّز الشحنة وسيرسل رقم التتبع قريباً.' : lang === 'zh' ? '供应商已备货完毕，物流单号将很快提供。' : 'The supplier has prepared your shipment. Tracking will appear here shortly.',
                  };
                  if (r.status === 'shipping') return {
                    label: isAr ? 'في الطريق إليك' : lang === 'zh' ? '运输中' : 'On the Way',
                    color: 'var(--warn)',
                    bg: 'rgba(160,136,80,0.05)',
                    border: 'rgba(160,136,80,0.30)',
                    body: isAr ? 'الشحنة في الطريق. تابع رقم التتبع، وأكد الاستلام عند الوصول.' : lang === 'zh' ? '货物正在运送途中。请使用物流单号跟踪，到货后请确认。' : 'Your shipment is on the way. Track it below and mark it as arrived once it lands.',
                  };
                  if (r.status === 'arrived') return {
                    label: isAr ? 'وصلت — أكد الاستلام' : lang === 'zh' ? '已到货 — 请确认收货' : 'Arrived — Confirm Delivery',
                    color: 'var(--green)',
                    bg: 'rgba(45,122,79,0.05)',
                    border: 'rgba(45,122,79,0.30)',
                    body: isAr ? 'وصلت الشحنة. عند فحصها وتأكيد سلامتها، أكد الاستلام لتحرير المبلغ للمورد.' : lang === 'zh' ? '货物已到达。检查无误后请确认收货，款项将放给供应商。' : 'The shipment arrived. After inspection, confirm delivery to release the payment to the supplier.',
                  };
                  if (r.status === 'delivered') return {
                    label: isAr ? 'مكتمل ✓' : lang === 'zh' ? '已完成 ✓' : 'Completed ✓',
                    color: 'var(--green)',
                    bg: 'rgba(45,122,79,0.04)',
                    border: 'rgba(45,122,79,0.25)',
                    body: isAr ? `تم التسليم بنجاح في ${relativeTime(r.updated_at, isAr)}. تم تحويل المبلغ للمورد.` : lang === 'zh' ? `订单已于 ${relativeTime(r.updated_at, isAr)} 完成交付，款项已放给供应商。` : `Delivered ${relativeTime(r.updated_at, isAr)}. Payout has been released to the supplier.`,
                  };
                  return {
                    label: r.status,
                    color: 'var(--text-disabled)',
                    bg: 'var(--bg-subtle)',
                    border: 'var(--border-subtle)',
                    body: '',
                  };
                })();

                return (
                  <div key={r.id} style={{ marginBottom: 14, border: `1px solid ${statusInfo.border}`, background: statusInfo.bg, borderRadius: 'var(--radius-lg)', padding: '20px 22px' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                      {productImage && (
                        <div style={{ width: 88, height: 88, borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--bg-subtle)', flexShrink: 0 }}>
                          <img src={productImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-raised)', border: `1px solid ${statusInfo.border}`, color: statusInfo.color, letterSpacing: 0.4, fontWeight: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {statusInfo.label}
                          </span>
                          <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>{relativeTime(r.created_at, isAr)}</span>
                        </div>
                        <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {productName || (isAr ? 'منتج' : 'Product')}
                        </h3>
                        <div style={{ display: 'flex', gap: 14, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          <span>{isAr ? 'المورد:' : lang === 'zh' ? '供应商：' : 'Supplier:'} {supplierName}</span>
                          <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {qty || '—'}</span>
                          {unitPrice > 0 && (
                            <span style={{ direction: 'ltr' }}>{toSarText(unitPrice, currency)} / {isAr ? 'وحدة' : 'unit'}</span>
                          )}
                          {totalEstimate > 0 && (
                            <span style={{ direction: 'ltr', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {isAr ? 'الإجمالي:' : lang === 'zh' ? '总计：' : 'Total:'} {toSarText(totalEstimate, currency)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {statusInfo.body && (
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {statusInfo.body}
                      </p>
                    )}

                    {/* Phase 5D — Trade Terms confirmation log: the supplier-side fields the
                       buyer agreed to when this order was placed. Hidden when none is set. */}
                    {(() => {
                      const tT = T[lang] || T.en;
                      const incoterms = Array.isArray(product.incoterms) ? product.incoterms.filter(Boolean) : [];
                      const port = String(product.port_of_loading || '').trim();
                      const country = String(product.country_of_origin || '').trim();
                      const lmin = product.lead_time_min_days != null ? Number(product.lead_time_min_days) : null;
                      const lmax = product.lead_time_max_days != null ? Number(product.lead_time_max_days) : null;
                      const negotiable = Boolean(product.lead_time_negotiable);
                      const hasAny = incoterms.length > 0 || port || country || lmin !== null || lmax !== null;
                      if (!hasAny) return null;

                      const portLocalized = port ? (tT[`port${port}`] || port) : null;
                      const countryLocalized = country ? (tT[`country${country.replace(/[^A-Za-z]/g, '')}`] || country) : null;

                      return (
                        <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                          <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {tT.buyerOrderTermsTitle}
                          </p>
                          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {incoterms.length > 0 && (
                              <span style={{ color: 'var(--text-secondary)' }}>
                                <span style={{ color: 'var(--text-disabled)', marginInlineEnd: 4 }}>{tT.buyerIncotermsLabel}:</span>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{incoterms.join(' · ')}</span>
                              </span>
                            )}
                            {portLocalized && (
                              <span style={{ color: 'var(--text-secondary)' }}>
                                <span style={{ color: 'var(--text-disabled)', marginInlineEnd: 4 }}>{tT.buyerPortOfLoadingLabel}:</span>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{portLocalized}</span>
                              </span>
                            )}
                            {(lmin !== null || lmax !== null) && (
                              <span style={{ color: 'var(--text-secondary)' }}>
                                <span style={{ color: 'var(--text-disabled)', marginInlineEnd: 4 }}>{tT.buyerLeadTimeLabel}:</span>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{tT.leadTimeBuyerFn(lmin, lmax, negotiable)}</span>
                              </span>
                            )}
                            {countryLocalized && (
                              <span style={{ color: 'var(--text-secondary)' }}>
                                <span style={{ color: 'var(--text-disabled)', marginInlineEnd: 4 }}>{tT.countryOfOriginLabel}:</span>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{countryLocalized}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tracking panel — visible whenever a tracking number has been uploaded */}
                    {r.tracking_number && ['shipping', 'arrived', 'delivered'].includes(r.status) && (() => {
                      const carrier = r.shipping_company || 'Other';
                      const trackUrl = getTrackingUrl(carrier, r.tracking_number);
                      return (
                        <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                          <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8, fontFamily: 'var(--font-sans)' }}>
                            {isAr ? 'تتبع الشحنة' : lang === 'zh' ? '物流跟踪' : 'Shipment Tracking'}
                          </p>
                          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {isAr ? 'شركة الشحن:' : lang === 'zh' ? '承运商：' : 'Carrier:'} <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{carrier}</strong>
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {isAr ? 'رقم التتبع:' : lang === 'zh' ? '物流单号：' : 'Tracking #:'} <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-en)', fontWeight: 600, direction: 'ltr', display: 'inline-block' }}>{r.tracking_number}</strong>
                            </span>
                            <a href={trackUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--green)', textDecoration: 'none', borderBottom: '1px dashed var(--green)', paddingBottom: 1 }}>
                              {isAr ? 'تتبع الشحنة ↗' : lang === 'zh' ? '查看跟踪 ↗' : 'Track Shipment ↗'}
                            </a>
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {['paid', 'shipping', 'arrived', 'delivered'].includes(r.status) && (
                        <button
                          onClick={() => setInvoiceOrder({ requestId: r.id, order: { request_ref: r.request_ref, buyer_name: profile?.company_name || profile?.full_name || '', supplier_name: supplierName, product_name: productName, quantity: qty, unit_price: unitPrice, currency } })}
                          style={{ padding: '11px 18px', fontSize: 13, fontWeight: 600, minHeight: 44, background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'الفاتورة' : lang === 'zh' ? '发票' : 'Invoice'}
                        </button>
                      )}
                      {r.status === 'supplier_confirmed' && (
                        <>
                          <button
                            onClick={() => payDirectOrder(r, 'deposit')}
                            disabled={paying || !product?.supplier_id}
                            style={{ padding: '11px 18px', fontSize: 13, fontWeight: 600, minHeight: 44, background: 'none', border: '1px solid var(--green)', borderRadius: 'var(--radius-md)', color: 'var(--green)', cursor: 'pointer', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {isAr ? 'ادفع العربون (٣٠٪)' : lang === 'zh' ? '支付定金 (30%)' : 'Pay deposit (30%)'}
                          </button>
                          <button
                            onClick={() => payDirectOrder(r, 'full')}
                            disabled={paying || !product?.supplier_id}
                            className="btn-primary"
                            style={{ padding: '11px 22px', fontSize: 13, fontWeight: 600, minHeight: 44, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : 0.3 }}>
                            {paying ? '…' : isAr ? 'ادفع كامل ←' : lang === 'zh' ? '全额付款 →' : 'Pay in full →'}
                          </button>
                        </>
                      )}
                      {r.status === 'shipping' && (
                        <button
                          onClick={() => markDirectOrderArrived(r)}
                          disabled={Boolean(directOrderActioning[r.id]) || !product?.supplier_id}
                          className="btn-primary"
                          style={{ padding: '11px 22px', fontSize: 13, fontWeight: 600, minHeight: 44, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : 0.3 }}>
                          {directOrderActioning[r.id] === 'marking_arrived' ? '…' : isAr ? 'تم استلام الشحنة' : lang === 'zh' ? '已收到货物' : 'Mark as Arrived'}
                        </button>
                      )}
                      {r.status === 'arrived' && (
                        <button
                          onClick={() => confirmDirectDelivery(r)}
                          disabled={Boolean(directOrderActioning[r.id]) || !product?.supplier_id}
                          style={{ padding: '11px 22px', fontSize: 13, fontWeight: 600, minHeight: 44, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : 0.3, background: 'var(--green)', color: 'var(--on-dark)', border: 'none', borderRadius: 'var(--radius-md)', cursor: directOrderActioning[r.id] ? 'not-allowed' : 'pointer' }}>
                          {directOrderActioning[r.id] === 'confirming_delivery' ? '…' : isAr ? 'تأكيد الاستلام' : lang === 'zh' ? '确认收货' : 'Confirm Delivery'}
                        </button>
                      )}
                      {r.status === 'delivered' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'rgba(45,122,79,0.10)', border: '1px solid rgba(45,122,79,0.30)', borderRadius: 'var(--radius-md)', color: 'var(--green)', fontSize: 12, fontWeight: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          ✓ {isAr ? `مكتمل · ${relativeTime(r.updated_at, isAr)}` : lang === 'zh' ? `已完成 · ${relativeTime(r.updated_at, isAr)}` : `Completed · ${relativeTime(r.updated_at, isAr)}`}
                        </span>
                      )}
                      {r.status === 'supplier_rejected' && (
                        <button onClick={() => nav(hioBuyOnly ? '/market' : '/products')} className="btn-outline" style={{ padding: '9px 18px', fontSize: 12, minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'تصفح المنتجات' : lang === 'zh' ? '浏览产品' : 'Browse Products'}
                        </button>
                      )}
                      {product?.supplier_id && r.status !== 'supplier_rejected' && r.status !== 'delivered' && (
                        <button onClick={() => nav(`/chat/${product.supplier_id}`)} className="btn-outline" style={{ padding: '9px 18px', fontSize: 12, minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'تواصل مع المورد' : lang === 'zh' ? '联系供应商' : 'Chat with Supplier'}
                        </button>
                      )}
                      {product?.id && (
                        <button onClick={() => nav(`/products/${product.id}`)} className="btn-outline" style={{ padding: '9px 18px', fontSize: 12, minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'عرض المنتج' : lang === 'zh' ? '查看产品' : 'View Product'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── SAMPLES ── */}
          {activeTab === 'samples' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} isAr={isAr} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'طلبات العينات' : 'Sample Requests'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 28, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr ? 'تابع حالة العينات وتواصل مع المورد بعد الموافقة.' : 'Track your sample requests and contact the supplier after approval.'}
              </p>

              {samples.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 13, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'لا توجد طلبات عينات بعد' : 'No sample requests yet'}
                  </p>
                </div>
              ) : samples.map((s, idx) => {
                const supplierName = s.profiles?.company_name || s.profiles?.full_name || 'Supplier';
                const productName = s.products?.name_ar || s.products?.name_en || s.products?.name_zh || 'Product';
                const supplierTrustSignals = buildSupplierTrustSignals(s.profiles || {});
                const sampleSupplierMaabarId = getSupplierMaabarId(s.profiles || {});
                return (
                  <div key={s.id} style={{ borderTop: '1px solid var(--border-subtle)', padding: '18px 0', animation: `fadeIn 0.35s ease ${idx * 0.04}s both` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 5, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{productName}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? 'المورد:' : lang === 'zh' ? '供应商：' : 'Supplier:'} {supplierName}</p>
                        </div>
                        {(sampleSupplierMaabarId || s.profiles?.city || s.profiles?.country || s.profiles?.years_experience) && (
                          <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 6, lineHeight: 1.6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {sampleSupplierMaabarId ? `${isAr ? 'معرّف المورد' : lang === 'zh' ? '供应商编号' : 'Supplier ID'}: ${sampleSupplierMaabarId}` : ''}
                            {sampleSupplierMaabarId && (s.profiles?.city || s.profiles?.country) ? ' · ' : ''}
                            {[s.profiles?.city, s.profiles?.country].filter(Boolean).join(', ')}
                            {(sampleSupplierMaabarId || s.profiles?.city || s.profiles?.country) && s.profiles?.years_experience ? ' · ' : ''}
                            {s.profiles?.years_experience ? (isAr ? `${s.profiles.years_experience} سنة خبرة` : lang === 'zh' ? `${s.profiles.years_experience} 年经验` : `${s.profiles.years_experience} years`) : ''}
                          </p>
                        )}
                      </div>
                      <span style={{ fontSize: 11, padding: '5px 10px', borderRadius: 'var(--radius-chip)', background: s.status === 'approved' ? 'rgba(45,122,79,0.1)' : s.status === 'rejected' ? 'rgba(224,92,92,0.1)' : 'var(--bg-subtle)', color: s.status === 'approved' ? 'var(--green)' : s.status === 'rejected' ? 'var(--red)' : 'var(--text-secondary)' }}>
                        {s.status === 'approved' ? (isAr ? 'تمت الموافقة' : lang === 'zh' ? '已批准' : 'Approved') : s.status === 'rejected' ? (isAr ? 'مرفوضة' : lang === 'zh' ? '已拒绝' : 'Rejected') : (isAr ? 'قيد المراجعة' : lang === 'zh' ? '审核中' : 'Pending')}
                      </span>
                    </div>

                    {(supplierTrustSignals.length > 0 || s.profiles?.rating > 0) && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {s.profiles?.rating > 0 && (
                          <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 'var(--radius-chip)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                            ★ {s.profiles.rating.toFixed(1)}{s.profiles?.reviews_count ? ` · ${s.profiles.reviews_count}` : ''}
                          </span>
                        )}
                        {supplierTrustSignals.includes('factory_media_available') && (
                          <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 'var(--radius-chip)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                            {isAr ? 'صور مصنع' : lang === 'zh' ? '工厂图片' : 'Factory photos'}
                          </span>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {s.quantity}</span>
                      <span>{isAr ? 'الإجمالي:' : lang === 'zh' ? '总额：' : 'Total:'} {s.total_price} SAR</span>
                    </div>
                    {s.notes && (
                      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>💬 {s.notes}</p>
                    )}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {s.status === 'approved' && (
                        // Samples are paid through a chat invoice the supplier
                        // issues (owner's decision, 2026-09-26) — the invoice card
                        // in the conversation opens Telr. The old button sent the
                        // trader to the Moyasar /checkout page.
                        <p style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 4px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'للدفع: اطلب من المورد إصدار فاتورة العيّنة في المحادثة، وادفعها من هناك.'
                            : lang === 'zh' ? '付款方式：请供应商在聊天中开具样品发票，然后在聊天中付款。'
                            : 'To pay: ask the supplier to issue the sample invoice in the chat, then pay it there.'}
                        </p>
                      )}
                      <button className="btn-outline" onClick={() => nav(`/supplier/${s.supplier_id}`)} style={{ minHeight: 34, fontSize: 11 }}>
                        {isAr ? 'ملف المورد' : lang === 'zh' ? '供应商主页' : 'Supplier Profile'}
                      </button>
                      <button className="btn-outline" onClick={() => nav(`/chat/${s.supplier_id}`)} style={{ minHeight: 34, fontSize: 11 }}>
                        {isAr ? 'محادثة المورد' : lang === 'zh' ? '联系供应商' : 'Chat Supplier'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── PRODUCT INQUIRIES ── */}
          {activeTab === 'product-inquiries' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} isAr={isAr} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 14, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'استفسارات المنتجات' : lang === 'zh' ? '产品咨询' : 'Product Inquiries'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 28, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', maxWidth: 760 }}>
                {isAr
                  ? 'كل استفسار ترسله من صفحة المنتج يظهر هنا، وعندما يرد المورد ستشاهد الرد داخل النظام بالإضافة إلى وصوله على بريدك.'
                  : lang === 'zh'
                    ? '您从产品页发送的咨询都会显示在这里。供应商回复后，您可以在系统内看到完整记录，邮件也会同步送达。'
                    : 'Every inquiry you send from a product page appears here. When the supplier replies, you will see it inside the system and also receive it by email.'}
              </p>

              {productInquiries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 13, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'لا توجد استفسارات منتجات بعد' : lang === 'zh' ? '暂时没有产品咨询' : 'No product inquiries yet'}
                  </p>
                </div>
              ) : productInquiries.map((inquiry, idx) => {
                const supplierName = inquiry.profiles?.company_name || inquiry.profiles?.full_name || (isAr ? 'مورد' : lang === 'zh' ? '供应商' : 'Supplier');
                const productName = getProductInquiryProductName(inquiry.products, lang);
                const statusLabel = getProductInquiryStatusLabel(inquiry.status, lang, 'buyer');
                const replies = inquiry.product_inquiry_replies || [];
                return (
                  <div key={inquiry.id} style={{ borderTop: '1px solid var(--border-subtle)', padding: '22px 0', animation: `fadeIn 0.35s ease ${idx * 0.04}s both` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{productName}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 4 }}>{supplierName}</p>
                        <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.8, margin: 0, fontFamily: 'var(--font-ar)' }}>{inquiry.question_text}</p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isAr ? 'flex-start' : 'flex-end', gap: 6 }}>
                        <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-subtle)', color: inquiry.status === 'answered' ? 'var(--green)' : 'var(--text-secondary)' }}>
                          {statusLabel}
                        </span>
                        <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: 0 }}>{new Date(inquiry.updated_at || inquiry.created_at).toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : 'en-US')}</p>
                      </div>
                    </div>

                    {replies.length === 0 ? (
                      <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8, margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'بانتظار رد المورد.' : lang === 'zh' ? '等待供应商回复。' : 'Waiting for the supplier reply.'}
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 10 }}>
                        {replies.map((reply) => (
                          <div key={reply.id} style={{ padding: '12px 14px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
                            <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 6 }}>
                              {isAr ? 'رد المورد' : lang === 'zh' ? '供应商回复' : 'Supplier reply'}
                            </p>
                            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{reply.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      {inquiry.product_id && (
                        <button className="btn-outline" onClick={() => nav(`/products/${inquiry.product_id}`)}>
                          {isAr ? 'فتح المنتج' : lang === 'zh' ? '打开产品' : 'Open Product'}
                        </button>
                      )}
                      {inquiry.supplier_id && (
                        <button className="btn-outline" onClick={() => nav(`/chat/${inquiry.supplier_id}`)}>
                          {isAr ? 'محادثة المورد' : lang === 'zh' ? '联系供应商' : 'Chat Supplier'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── MESSAGES ── */}

          {/* ── SETTINGS ── */}
          {activeTab === 'settings' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} isAr={isAr} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 32, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'إعدادات الحساب' : 'Account Settings'}
              </h2>

              <div style={{
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border-muted)',
                padding: '28px 32px',
                maxWidth: 560,
                borderRadius: 'var(--radius-xl)',
              }}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'الاسم الكامل' : 'Full Name'}</label>
                    <input className="form-input" value={settings.full_name} onChange={e => setSettings({ ...settings, full_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{isAr ? 'رقم الجوال' : 'Phone'}</label>
                    <input className="form-input" value={settings.phone} onChange={e => setSettings({ ...settings, phone: e.target.value })} placeholder="+966 5x xxx xxxx" dir="ltr" />
                  </div>
                  <div className="form-group">
                    <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'المدينة' : 'City'}</label>
                    <select className="form-input" value={settings.city} onChange={e => setSettings({ ...settings, city: e.target.value })}>
                      <option value="">{isAr ? 'اختر مدينة' : 'Select city'}</option>
                      {(isAr ? CITIES_AR : CITIES_EN).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'اسم الشركة / النشاط' : 'Company / Business'}</label>
                    <input className="form-input" value={settings.company_name} onChange={e => setSettings({ ...settings, company_name: e.target.value })} placeholder={isAr ? 'اختياري' : 'Optional'} />
                  </div>
                  {/* The "Preferred Display Currency" select is GONE (owner,
                      2026-09-29): traders see SAR only, everywhere. */}
                </div>

                <button onClick={saveSettings} disabled={savingSettings} className="btn-primary"
                  style={{ padding: '11px 28px', fontSize: 13, marginTop: 8, minHeight: 44 }}>
                  {savingSettings ? '...' : isAr ? 'حفظ التغييرات' : 'Save Changes'}
                </button>
                {settingsSaved && (
                  <p style={{ color: 'var(--green)', fontSize: 13, marginTop: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? 'تم حفظ التغييرات ✓' : 'Changes saved ✓'}
                  </p>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══════════════════════════════════════
          REVIEW MODAL
      ══════════════════════════════════════ */}
      {reviewModal && (
        <div className="mm-overlay" style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 3000,
          animation: 'fadeIn 0.2s ease',
        }}>
          <div className="mm-card" style={{
            background: 'var(--bg-overlay)',
            border: '1px solid var(--border-muted)',
            borderRadius: 'var(--radius-xl)',
            maxWidth: 400,
            animation: 'slideUp 0.25s ease',
            boxShadow: 'none',
          }}>
            <div className="mm-body" style={{ padding: '36px 32px 8px' }}>
            <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14 }}>
              {isAr ? 'تقييم المورد' : 'Rate Supplier'}
            </p>
            <h3 style={{ fontSize: 18, fontWeight: 400, marginBottom: 6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)' }}>
              {isAr ? `كيف كانت تجربتك مع ${reviewModal.supplierName}؟` : `How was your experience with ${reviewModal.supplierName}?`}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-disabled)', marginBottom: 24, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', lineHeight: 1.7 }}>
              {isAr ? 'تقييمك يساعد التجار الآخرين على اختيار الموردين الموثوقين' : 'Your review helps other traders choose reliable suppliers'}
            </p>

            {/* Stars */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 24, justifyContent: 'center' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button key={star} onClick={() => setReviewRating(star)} style={{
                  fontSize: 32, background: 'none', border: 'none', cursor: 'pointer',
                  color: star <= reviewRating ? 'var(--warn)' : 'var(--border-default)',
                  transition: 'all 0.15s',
                  transform: star <= reviewRating ? 'scale(1.1)' : 'scale(1)',
                }}>★</button>
              ))}
            </div>

            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'تعليق (اختياري)' : 'Comment (optional)'}</label>
              <textarea className="form-input" rows={3}
                style={{ resize: 'none', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder={isAr ? 'شاركنا تجربتك...' : 'Share your experience...'}
              />
            </div>

            </div>
            <div className="mm-foot" style={{ display: 'flex', gap: 10, padding: '16px 32px 28px', background: 'var(--bg-overlay)' }}>
              <button onClick={submitReview} disabled={!reviewRating || submittingReview} className="btn-primary"
                style={{ flex: 1, padding: '11px', fontSize: 12, minHeight: 44, opacity: !reviewRating ? 0.4 : 1 }}>
                {submittingReview ? '...' : isAr ? 'إرسال التقييم' : 'Submit Review'}
              </button>
              <button onClick={() => setReviewModal(null)} className="btn-outline"
                style={{ padding: '11px 18px', fontSize: 12, minHeight: 44 }}>
                {isAr ? 'تخطي' : 'Skip'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          EDIT REQUEST MODAL
      ══════════════════════════════════════ */}
      {editReqModal && (
        <div className="mm-overlay" style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 3000,
          animation: 'fadeIn 0.2s ease',
        }}>
          <div className="mm-card" style={{
            background: 'var(--bg-overlay)',
            border: '1px solid var(--border-muted)',
            borderRadius: 'var(--radius-xl)',
            maxWidth: 480,
            animation: 'slideUp 0.25s ease',
            boxShadow: 'none',
          }}>
            <div className="mm-body" style={{ padding: '36px 32px 8px' }}>
            <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14 }}>
              {isAr ? 'تعديل الطلب' : 'Edit Request'}
            </p>

            <div className="form-grid" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'العنوان بالعربي' : 'Title (Arabic)'}</label>
                <input className="form-input" dir="rtl" value={editReqForm.title_ar}
                  onChange={e => setEditReqForm(f => ({ ...f, title_ar: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{isAr ? 'العنوان بالإنجليزي' : 'Title (English)'}</label>
                <input className="form-input" dir="ltr" value={editReqForm.title_en}
                  onChange={e => setEditReqForm(f => ({ ...f, title_en: e.target.value }))} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'الوصف' : 'Description'}</label>
                <textarea className="form-input" rows={3} style={{ resize: 'none', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  value={editReqForm.desc_ar}
                  onChange={e => setEditReqForm(f => ({ ...f, desc_ar: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'الكمية *' : lang === 'zh' ? '数量 *' : 'Quantity *'}</label>
                <input className="form-input" inputMode="decimal" dir="ltr" value={editReqForm.quantity}
                  onChange={e => setEditReqForm(f => ({ ...f, quantity: normalizeQuantityInput(e.target.value) }))} />
              </div>
            </div>
            {editReqError && <Notice tone="error" isAr={isAr}>{editReqError}</Notice>}

            </div>
            <div className="mm-foot" style={{ display: 'flex', gap: 10, padding: '16px 32px 28px', background: 'var(--bg-overlay)' }}>
              <button onClick={saveEditRequest} disabled={savingEditReq} className="btn-primary"
                style={{ flex: 1, padding: '11px', fontSize: 12, minHeight: 44 }}>
                {savingEditReq ? '...' : isAr ? 'حفظ التغييرات' : 'Save Changes'}
              </button>
              <button onClick={() => setEditReqModal(null)} className="btn-outline"
                style={{ padding: '11px 18px', fontSize: 12, minHeight: 44 }}>
                {isAr ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          CANCEL REQUEST MODAL
      ══════════════════════════════════════ */}
      {cancelConfirmReq && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24, animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{
            background: 'var(--bg-overlay)',
            border: '1px solid var(--border-muted)',
            borderRadius: 'var(--radius-xl)',
            padding: '36px 32px',
            width: '100%', maxWidth: 400,
            animation: 'slideUp 0.25s ease',
            boxShadow: 'none',
          }}>
            <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14 }}>
              {isAr ? 'إلغاء الطلب' : 'Cancel Request'}
            </p>
            <h3 style={{ fontSize: 18, fontWeight: 400, marginBottom: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', color: 'var(--text-primary)' }}>
              {isAr
                ? (cancelConfirmReq.title_ar || cancelConfirmReq.title_en)
                : (cancelConfirmReq.title_en || cancelConfirmReq.title_ar)}
            </h3>
            {/* ⚠️ THIS COPY DESCRIBED BEHAVIOUR THAT NO LONGER EXISTS, and it
                was the last thing still telling the trader the old story:
                cancel USED TO re-open the request, which is the exact route the
                three-stage rule closed. The code stopped doing it; the dialog
                went on promising it.

                The refund warning is gone entirely. "Once payment is made, the
                order cannot be cancelled or refunded" implies a refund flow
                that does not exist in this product, and it was warning about a
                state this dialog can no longer reach: after payment there is no
                cancel button at all (stage 3). A warning about an impossible
                action is just a false promise with a negative sign. */}
            <p style={{ fontSize: 13, color: 'var(--text-disabled)', marginBottom: 20, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', lineHeight: 1.7 }}>
              {isAr
                ? 'سيُغلق هذا الطلب ولن يستقبل عروضاً جديدة. سيُبلَّغ كل مورّد قدّم عرضاً، وتبقى عروضهم وطلبك محفوظة. لا يمكن التراجع عن هذا.'
                : lang === 'zh'
                  ? '此需求将被关闭，不再接收新报价。已报价的供应商都会收到通知，他们的报价和您的需求都会保留。此操作无法撤销。'
                  : 'This closes the request — it will not receive new quotes. Every supplier who quoted is told, and their quotes and your request are kept. This cannot be undone.'}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => cancelRequest(cancelConfirmReq)}
                style={{ flex: 1, padding: '11px', fontSize: 12, minHeight: 44, background: 'none', border: '1px solid rgba(224,92,92,0.4)', color: 'var(--red)', cursor: 'pointer', borderRadius: 'var(--radius-md)' }}>
                {isAr ? 'نعم، إلغاء الطلب' : 'Yes, Cancel Request'}
              </button>
              <button onClick={() => setCancelConfirmReq(null)} className="btn-outline"
                style={{ padding: '11px 18px', fontSize: 12, minHeight: 44 }}>
                {isAr ? 'رجوع' : 'Go Back'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer lang={lang} />

      {invoiceOrder && (
        <OrderInvoiceModal
          requestId={invoiceOrder.requestId}
          order={invoiceOrder.order}
          role="buyer"
          lang={lang}
          onClose={() => setInvoiceOrder(null)}
        />
      )}
    </div>
  );
}